import { renewalInvoiceForm } from '../utils/renewalInvoiceForm';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import CountryPhoneInput from '../components/CountryPhoneInput';
import SearchableDropdown from '../components/SearchableDropdown';
import axios from 'axios';
import { trackFeatureUsage, trackFormSubmission, trackEvent, trackDownload } from '../utils/analytics';
import { calculateAgeFromDateString, compareDateOnlyDesc, formatDateForDisplay, formatDateForInput, getCurrentDateForInput } from '../utils/dateFormat';
import { calculateIdealWeight, getIdealHealthValues } from '../utils/healthCalculations';
import PartialPaymentModal from '../components/PartialPaymentModal';
import CashbackModal from '../components/CashbackModal';
import ClubChangeModal from '../components/ClubChangeModal';
import HealthProgressGraphModal from '../components/HealthProgressGraphModal';
import DailyLogMonitoringModal from '../components/DailyLogMonitoringModal';
import ProductSettlementsPanel from '../components/ProductSettlementsPanel';
import BadgeLogo from '../components/BadgeLogo';

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const formatWeightLossValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return null;
  }

  const formattedValue = Math.abs(numericValue).toFixed(1).replace(/\.0$/, '');
  return `${numericValue >= 0 ? '-' : '+'}${formattedValue} kg`;
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === 'string') {
    return data;
  }
  return data?.message || fallback;
};

const CustomerDetails = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [renewalDraftId, setRenewalDraftId] = useState(null);
  const { user } = useAuth();
  const { t } = useLanguage();
  const [customer, setCustomer] = useState(null);
  const [healthReadings, setHealthReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [healthReadingsLoading, setHealthReadingsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddReading, setShowAddReading] = useState(false);
  const [showHealthGraph, setShowHealthGraph] = useState(false);
  const [showLogMonitoring, setShowLogMonitoring] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [newReading, setNewReading] = useState({
    readingDate: getCurrentDateForInput(),
    weight: '',
    visceralFat: '',
    tsf: '',
    totalFatPercentage: '',
    metaAge: '',
    bmi: '',
    bmr: '',
    skeletalMusclePercentage: ''
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [activeTab, setActiveTab] = useState('health');
  const [healthReports, setHealthReports] = useState([]);
  const [healthReportsLoading, setHealthReportsLoading] = useState(false);
  const [userPrograms, setUserPrograms] = useState([]);
  const [userProgramsLoading, setUserProgramsLoading] = useState(false);
  const [showAssignProgramModal, setShowAssignProgramModal] = useState(false);
  const [assignProgramData, setAssignProgramData] = useState({
    programId: '',
    startDate: getCurrentDateForInput(),
    endDate: '',
    comments: ''
  });
  const [assignProgramLoading, setAssignProgramLoading] = useState(false);
  const [assignProgramError, setAssignProgramError] = useState('');
  const [showStopProgramModal, setShowStopProgramModal] = useState(false);
  const [selectedProgramToStop, setSelectedProgramToStop] = useState(null);
  const [stopProgramComments, setStopProgramComments] = useState('');
  const [stopProgramLoading, setStopProgramLoading] = useState(false);
  const [showGenerateReportModal, setShowGenerateReportModal] = useState(false);
  const [generateReportLoading, setGenerateReportLoading] = useState(false);
  const [generateReportError, setGenerateReportError] = useState('');
  const [reportGenerationMessages] = useState([
    "Sit back and let AI analyze your data...",
    "Crunching numbers and finding patterns...",
    "AI is reviewing your health journey...",
    "Generating personalized insights...",
    "Almost done, creating your report...",
    "Finalizing your health analysis...",
    "AI is working its magic on your data...",
    "Analyzing trends and progress...",
    "Creating your personalized health story...",
    "Putting together your wellness roadmap..."
  ]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showAddInvoice, setShowAddInvoice] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [newInvoice, setNewInvoice] = useState({
    invoiceDate: getCurrentDateForInput(),
    dueDate: '',
    description: '',
    status: 'PAID',
    paymentDate: '',
    paymentMode: '',
    paymentType: 'FULL',
    paidAmount: '',
    notes: '',
    lineItems: []
  });
  const [invoiceType, setInvoiceType] = useState('PROGRAM'); // PRODUCT or PROGRAM
  const [products, setProducts] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [baseCompanyInfo, setCompanyInfo] = useState(null);
  const [renewalDraftCompany, setRenewalDraftCompany] = useState(null);
  const companyInfo = renewalDraftCompany || baseCompanyInfo;
  const [currentLineItem, setCurrentLineItem] = useState({
    itemType: 'PRODUCT',
    itemId: '',
    quantity: 1
  });
  const [gstCalculations, setGstCalculations] = useState({
    subtotal: 0,
    totalCgst: 0,
    totalSgst: 0,
    totalDiscount: 0,
    totalAmount: 0,
    subtotalBeforeCashback: 0,
    programProductDiscount: 0
  });
  const [cashbackBalance, setCashbackBalance] = useState(null);
  const [appliedCashback, setAppliedCashback] = useState(0);
  const [customDiscountEnabled, setCustomDiscountEnabled] = useState(false);
  const [customDiscountAmount, setCustomDiscountAmount] = useState('');
  const [shippingEnabled, setShippingEnabled] = useState(false);
  const [shippingCharges, setShippingCharges] = useState('');
  const [showEditCustomer, setShowEditCustomer] = useState(false);
  const [editCustomerData, setEditCustomerData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    username: '',
    password: '',
    gender: 'MALE',
    phone: '',
    phoneCountryId: null,
    address: '',
    gstNumber: '',
    pcId: '',
    customerType: 'ONLINE',
    checkupDay: 'MONDAY',
    program: '',
    discountSlabId: '',
    memberRoleCode: 'CUSTOMER',
    dateOfBirth: '',
    age: '',
    heightCm: '',
    idealWeightKg: '',
    initialWeightKg: '',
    referredById: null,
    referredBySearch: '',
    showReferralDropdown: false,
    filteredReferralOptions: [],
    status: 'ACTIVE'
  });
  const [discountPlans, setDiscountPlans] = useState([]);
  const [productDiscountPlans, setProductDiscountPlans] = useState([]);
  const [programOptions, setProgramOptions] = useState([]);
  const [referralOptions, setReferralOptions] = useState([]);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [promoteLoading, setPromoteLoading] = useState(false);
  const [promoteError, setPromoteError] = useState('');
  const [showPartialPaymentModal, setShowPartialPaymentModal] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState(null);
  const [showCashbackModal, setShowCashbackModal] = useState(false);
  const [showCustomCashbackModal, setShowCustomCashbackModal] = useState(false);
  const [customCashbackStep, setCustomCashbackStep] = useState('details');
  const [customCashbackForm, setCustomCashbackForm] = useState({ amount: '', description: '', otp: '' });
  const [customCashbackRequest, setCustomCashbackRequest] = useState(null);
  const [pendingCustomCashbackRequests, setPendingCustomCashbackRequests] = useState([]);
  const [customCashbackHistory, setCustomCashbackHistory] = useState([]);
  const [customCashbackLoading, setCustomCashbackLoading] = useState(false);
  const [customCashbackError, setCustomCashbackError] = useState('');
  const [customCashbackSuccess, setCustomCashbackSuccess] = useState('');
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [selectedProgramAttendance, setSelectedProgramAttendance] = useState(null);
  const [attendanceDates, setAttendanceDates] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(false);
  const [mealPlans, setMealPlans] = useState([]);
  const [mealPlansLoading, setMealPlansLoading] = useState(false);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [customerBadges, setCustomerBadges] = useState([]);
  const [selectedBadgeId, setSelectedBadgeId] = useState('');
  const [badgesLoading, setBadgesLoading] = useState(false);
  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);
  const [showAddMealPlan, setShowAddMealPlan] = useState(false);
  const [editingMealPlan, setEditingMealPlan] = useState(null);
  const [weightCategories, setWeightCategories] = useState([]);
  const [foodPreferences, setFoodPreferences] = useState([]);
  const [selectedWeightCategory, setSelectedWeightCategory] = useState('');
  const [selectedFoodPreference, setSelectedFoodPreference] = useState('');
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [mealPlanData, setMealPlanData] = useState({
    mealPlanName: '',
    weightCategory: '',
    foodPreference: 'VEG',
    instructions: '',
    items: []
  });
  const [mealPlanLoading, setMealPlanLoading] = useState(false);
  const [mealPlanError, setMealPlanError] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [selectedMealPlanForShare, setSelectedMealPlanForShare] = useState(null);
  const [showClubChangeModal, setShowClubChangeModal] = useState(false);
  const [showProgramDetailsModal, setShowProgramDetailsModal] = useState(false);
  const [selectedProgramForDetails, setSelectedProgramForDetails] = useState(null);
  const [programDetailsComments, setProgramDetailsComments] = useState('');
  const [programDates, setProgramDates] = useState({startDate:'',endDate:''});
  const [programDateMessage, setProgramDateMessage] = useState('');
  const [programDetailsSaving, setProgramDetailsSaving] = useState(false);
  const [followups, setFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [followupForm, setFollowupForm] = useState({
    summary: '',
    nextFollowupAt: ''
  });
  const [followupSaving, setFollowupSaving] = useState(false);
  const [followupError, setFollowupError] = useState('');
  const [emailValidation, setEmailValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });
  const [usernameValidation, setUsernameValidation] = useState({
    isChecking: false,
    isValid: true,
    message: ''
  });

  useEffect(() => {
    fetchCustomerDetails();
    fetchDiscountPlans();
    fetchProductDiscountPlans();
    fetchCompanyInfo();
    fetchReferralOptions();
    fetchUserPrograms(); // Always fetch user programs for customer info section
    if (activeTab === 'health') {
      fetchHealthReadings();
    } else if (activeTab === 'invoices') {
      fetchInvoices();
      fetchProducts();
      fetchPrograms();
      fetchCashbackBalance(); // Fetch cashback balance when invoices tab is active
    } else if (activeTab === 'reports') {
      fetchHealthReports();
    } else if (activeTab === 'programs') {
      // User programs already fetched above, no need to fetch again
    } else if (activeTab === 'mealplan') {
      fetchMealPlans();
      fetchWeightCategories();
      fetchFoodPreferences();
    } else if (activeTab === 'recognition') {
      fetchRecognition();
    } else if (activeTab === 'referals') {
      fetchReferrals();
    } else if (activeTab === 'followups') {
      fetchFollowups();
    }
    
    // Track customer details page view
    trackFeatureUsage('Customer Details', 'view');
    trackEvent('customer_details_tab_viewed', {
      event_category: 'Customer Management',
      event_label: activeTab,
      customer_id: customerId
    });
  }, [customerId, activeTab]);

  useEffect(() => {
    if (canManageCustomCashback()) {
      fetchPendingCustomCashbackRequests();
    }

    if (isSystemAdmin()) {
      fetchCustomCashbackHistory();
    }
  }, [customerId, user, customer]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const validTabs = ['health', 'invoices', 'reports', 'programs', 'mealplan', 'recognition', 'referals', 'followups', 'settlements'];

    if (!tabParam || !validTabs.includes(tabParam)) {
      return;
    }

    setActiveTab(tabParam);
    urlParams.delete('tab');

    navigate(
      {
        pathname: location.pathname,
        search: urlParams.toString() ? `?${urlParams.toString()}` : ''
      },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const draftId = new URLSearchParams(location.search).get('renewalDraft');
    if (!draftId || !customer) return;
    let active = true;
    axios.get(`/api/renewal-drafts/${draftId}`).then(({ data: draft }) => {
      if (!active) return;
      const invoiceForm = renewalInvoiceForm(draft, customerId);
      setRenewalDraftCompany(draft.company);
      setRenewalDraftId(draft.id);
      setEditingInvoice(null);
      setInvoiceType('PROGRAM');
      setAppliedCashback(0);
      setShippingEnabled(false);
      setShippingCharges('');
      setCustomDiscountEnabled(Number(draft.customDiscount) > 0);
      setCustomDiscountAmount(String(draft.customDiscount || ''));
      setNewInvoice(invoiceForm);
      setActiveTab('invoices');
      setFormError('');
      setShowAddInvoice(true);
      const params = new URLSearchParams(location.search);
      params.delete('renewalDraft'); params.delete('tab');
      navigate({ pathname: location.pathname, search: params.toString() ? `?${params}` : '' }, { replace: true });
    }).catch(e => { if (active) setError(e.response?.data?.message || e.message || 'Unable to open draft invoice.'); });
    return () => { active = false; };
  }, [customer, customerId, location.pathname, location.search, navigate]);

  useEffect(() => {
    // Recalculate GST when line items change
    calculateGstTotals();
  }, [newInvoice.lineItems, companyInfo, programs, customer]);

  useEffect(() => {
    // Recalculate GST when custom discount is toggled
    calculateGstTotals();
  }, [customDiscountEnabled, customDiscountAmount]);

  useEffect(() => {
    // Recalculate GST when shipping charges change
    calculateGstTotals();
  }, [shippingEnabled, shippingCharges]);

  useEffect(() => {
    // Recalculate invoice totals when cashback is applied or removed
    calculateGstTotals();
  }, [appliedCashback]);

  useEffect(() => {
    // Update program options based on customer type
    if (editCustomerData.customerType === 'ONLINE') {
      setProgramOptions([
        'Basic 21 Days',
        'Advanced 21 Days',
        '30 Days Weight Loss'
      ]);
    } else if (editCustomerData.customerType === 'CLUB') {
      setProgramOptions([
        'Trial Program',
        'Fitness Plan',
        'Advanced Weight Loss',
        'Premium Weight Loss'
      ]);
    }
  }, [editCustomerData.customerType]);

  const fetchCustomerDetails = async () => {
    try {
      const response = await axios.get(`/api/customers/${customerId}`);
      setCustomer(response.data);
      
      // Initialize edit form data
      const customerData = response.data;
      setEditCustomerData({
        firstName: customerData.firstName || '',
        lastName: customerData.lastName || '',
        email: customerData.email || '',
        username: customerData.username || '',
        password: '', // Always start with empty password
        gender: customerData.gender || 'MALE',
        phone: customerData.phone || '',
        phoneCountryId: customerData.phoneCountry?.id || null,
        address: customerData.address || '',
        gstNumber: customerData.gstNumber || '',
        pcId: customerData.pcId || '',
        customerType: customerData.customerType || 'ONLINE',
        checkupDay: customerData.checkupDay || 'MONDAY',
        program: customerData.program || '',
        programDiscountSlabId: customerData.programDiscountPlan?.id || '',
        productDiscountSlabId: customerData.productDiscountPlan?.id || '',
        memberRoleCode: customerData.memberRole?.code || 'CUSTOMER',
        dateOfBirth: customerData.dateOfBirth ? formatDateForInput(customerData.dateOfBirth) : '',
        age: customerData.age?.toString() || '',
        heightCm: customerData.heightCm?.toString() || '',
        idealWeightKg: customerData.idealWeightKg?.toString() || '',
        initialWeightKg: customerData.initialWeightKg?.toString() || '',
        referredById: customerData.referredBy?.id || null,
        referredBySearch: customerData.referredBy?.fullName || '',
        showReferralDropdown: false,
        filteredReferralOptions: [],
        status: customerData.status || 'ACTIVE',
        discomfortsMedications: customerData.discomfortsMedications || ''
      });
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      setError('Failed to load customer details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscountPlans = async () => {
    try {
      const response = await axios.get('/api/products/discount-plans');
      setDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch discount plans:', error);
    }
  };

  const fetchProductDiscountPlans = async () => {
    try {
      const response = await axios.get('/api/products/product-discount-plans');
      setProductDiscountPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch product discount plans:', error);
    }
  };

  const fetchReferralOptions = async () => {
    try {
      const response = await axios.get('/api/customers/referral-options');
      setReferralOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch referral options:', error);
    }
  };

  const fetchCashbackBalance = async () => {
    try {
      const response = await axios.get(`/api/cashback/balance/${customerId}`);
      setCashbackBalance(response.data);
    } catch (error) {
      console.error('Failed to fetch cashback balance:', error);
      setCashbackBalance(null);
    }
  };

  const isSystemEditor = () => user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR');
  const isSystemAdmin = () => user?.systemRoles?.some(role => role.code === 'SYSTEM_ADMIN');
  const canManageCustomCashback = () => (isSystemEditor() || isSystemAdmin()) && customer?.memberRole?.code === 'CUSTOMER';

  const fetchPendingCustomCashbackRequests = async () => {
    try {
      const response = await axios.get(`/api/cashback/custom/${customerId}/pending`);
      setPendingCustomCashbackRequests(response.data || []);
    } catch (error) {
      console.error('Failed to fetch pending custom cashback requests:', error);
      setPendingCustomCashbackRequests([]);
    }
  };

  const fetchCustomCashbackHistory = async () => {
    try {
      const response = await axios.get(`/api/cashback/customer/${customerId}/history`);
      setCustomCashbackHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch customer cashback history:', error);
      setCustomCashbackHistory([]);
    }
  };

  const openCustomCashbackModal = async () => {
    setShowCustomCashbackModal(true);
    setCustomCashbackStep('details');
    setCustomCashbackForm({ amount: '', description: '', otp: '' });
    setCustomCashbackRequest(null);
    setCustomCashbackError('');
    setCustomCashbackSuccess('');
    await fetchPendingCustomCashbackRequests();
  };

  const handleRequestCustomCashback = async (e) => {
    e.preventDefault();
    setCustomCashbackLoading(true);
    setCustomCashbackError('');
    setCustomCashbackSuccess('');

    try {
      const response = await axios.post(`/api/cashback/custom/${customerId}/request`, {
        amount: parseFloat(customCashbackForm.amount),
        description: customCashbackForm.description
      });

      setCustomCashbackRequest(response.data);
      setCustomCashbackStep('otp');
      setCustomCashbackSuccess(t('OTP sent successfully'));
      await fetchPendingCustomCashbackRequests();
    } catch (error) {
      console.error('Failed to request custom cashback:', error);
      setCustomCashbackError(getApiErrorMessage(error, t('Failed to send OTP. Please check the amount, description, and phone number.')));
    } finally {
      setCustomCashbackLoading(false);
    }
  };

  const handleVerifyCustomCashback = async (e) => {
    e.preventDefault();
    const requestId = customCashbackRequest?.id;
    if (!requestId) {
      setCustomCashbackError(t('Please select a pending request to verify'));
      return;
    }

    setCustomCashbackLoading(true);
    setCustomCashbackError('');

    try {
      await axios.post(`/api/cashback/custom/${requestId}/verify`, {
        otp: customCashbackForm.otp
      });

      setCustomCashbackSuccess(t('Cashback added successfully'));
      setCustomCashbackStep('details');
      setCustomCashbackRequest(null);
      setCustomCashbackForm({ amount: '', description: '', otp: '' });
      await fetchPendingCustomCashbackRequests();
      await fetchCashbackBalance();
      if (isSystemAdmin()) {
        await fetchCustomCashbackHistory();
      }
    } catch (error) {
      console.error('Failed to verify custom cashback:', error);
      setCustomCashbackError(getApiErrorMessage(error, t('Invalid or expired OTP. Please try again.')));
    } finally {
      setCustomCashbackLoading(false);
    }
  };

  const selectPendingCustomCashbackRequest = (request) => {
    setCustomCashbackRequest(request);
    setCustomCashbackStep('otp');
    setCustomCashbackForm({
      amount: request.amount || '',
      description: request.description || '',
      otp: ''
    });
    setCustomCashbackError('');
    setCustomCashbackSuccess('');
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get('/api/products/all');
      setProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get('/api/products/programs');
      setPrograms(response.data);
    } catch (error) {
      console.error('Failed to fetch programs:', error);
    }
  };

  const fetchCompanyInfo = async () => {
    try {
      const response = await axios.get('/api/company');
      setCompanyInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch company info:', error);
    }
  };

  const getLineItemPricing = (lineItem) => {
    const itemType = lineItem.itemType;
    const quantity = parseInt(lineItem.quantity) || 0;
    const ignoreProgramSlabDiscount = invoiceType === 'PROGRAM' && customDiscountEnabled;

    let mrpPrice = 0;
    let discountedUnitPrice = parseFloat(lineItem.unitPrice) || 0;

    if (itemType === 'PRODUCT') {
      const selectedProduct = products.find(p => p.id === lineItem.itemId);
      if (selectedProduct) {
        mrpPrice = parseFloat(selectedProduct.mrp) || discountedUnitPrice;
      } else {
        mrpPrice = discountedUnitPrice;
      }
    } else {
      const selectedProgram = programs.find(p => p.id === lineItem.itemId);
      mrpPrice = parseFloat(selectedProgram?.programMrp) || discountedUnitPrice;

      if (!ignoreProgramSlabDiscount && (selectedProgram?.discountApplicable ?? lineItem.discountApplicable) && customer?.programDiscountPlan) {
        const discountPercentage = parseFloat(customer.programDiscountPlan.discountPercentage) || 0;
        discountedUnitPrice = mrpPrice - ((mrpPrice * discountPercentage) / 100);
      } else {
        discountedUnitPrice = mrpPrice;
      }
    }

    const lineMrpTotal = mrpPrice * quantity;
    const discountedTotal = discountedUnitPrice * quantity;
    const discountAmount = Math.max(0, lineMrpTotal - discountedTotal);

    return {
      quantity,
      mrpPrice,
      discountedUnitPrice,
      lineMrpTotal,
      discountedTotal,
      discountAmount
    };
  };

  const getProductPriceForPlan = (product, rawPlanCode) => {
    const planCode = (rawPlanCode || '').toUpperCase().replace(/[\s-]+/g, '_');

    switch (planCode) {
      case 'BRONZE':
        return product.bronzePrice;
      case 'SILVER':
        return product.silverPrice;
      case 'GOLD':
        return product.goldPrice;
      case 'SUCCESS_BUILDER':
      case 'SUCCESSBUILDER':
        return product.successBuilderPrice;
      case 'SUPERVISOR':
      case 'ASSOCIATE':
        return product.associatePrice;
      default:
        return product.mrp;
    }
  };

  const calculateGstTotals = () => {
    const gstApplicable = Boolean(companyInfo?.gstinNumber?.trim());
    let subtotalBeforeCashback = 0;
    let totalCgstBeforeCashback = 0;
    let totalSgstBeforeCashback = 0;
    let programProductDiscount = 0; // Separate program/product discounts from cashback

    // Custom discount for program invoices
    const customDiscount = (invoiceType === 'PROGRAM' && customDiscountEnabled) ? 
      (parseFloat(customDiscountAmount) || 0) : 0;

    if (customDiscountEnabled && customDiscount > 0) {
      // For custom discount: Calculate total MRP, subtract custom discount, then calculate GST
      let totalMrp = 0;
      
      newInvoice.lineItems.forEach(lineItem => {
        const quantity = parseInt(lineItem.quantity) || 0;
        const selectedProgram = programs.find(p => p.id === lineItem.itemId);
        if (selectedProgram) {
          totalMrp += selectedProgram.programMrp * quantity;
        }
      });
      
      // Final amount after custom discount
      const finalAmountAfterDiscount = Math.max(0, totalMrp - customDiscount);
      
      // Calculate GST on final amount (18% for programs)
      const gstRate = 18;
      const cgstRate = gstRate / 2;
      const sgstRate = gstRate / 2;
      
      // Calculate base price (excluding GST) from final amount
      subtotalBeforeCashback = gstApplicable
        ? finalAmountAfterDiscount / (1 + (gstRate / 100))
        : finalAmountAfterDiscount;
      totalCgstBeforeCashback = gstApplicable ? (subtotalBeforeCashback * cgstRate) / 100 : 0;
      totalSgstBeforeCashback = gstApplicable ? (subtotalBeforeCashback * sgstRate) / 100 : 0;
      programProductDiscount = customDiscount;
    } else {
      // Normal calculation without custom discount
      newInvoice.lineItems.forEach(lineItem => {
        const itemType = lineItem.itemType;
        const { discountedUnitPrice, discountedTotal, discountAmount } = getLineItemPricing(lineItem);

        // Calculate GST rates based on item type
        const gstRate = itemType === 'PRODUCT' ? 5 : 18; // 5% for products, 18% for programs
        const cgstRate = gstRate / 2;
        const sgstRate = gstRate / 2;

        // Calculate base price (excluding GST)
        const basePrice = gstApplicable ? discountedTotal / (1 + (gstRate / 100)) : discountedTotal;
        const cgstAmount = gstApplicable ? (basePrice * cgstRate) / 100 : 0;
        const sgstAmount = gstApplicable ? (basePrice * sgstRate) / 100 : 0;

        subtotalBeforeCashback += basePrice;
        totalCgstBeforeCashback += cgstAmount;
        totalSgstBeforeCashback += sgstAmount;
        programProductDiscount += discountAmount;
      });
    }
    
    // Cashback should reduce the payable amount directly without changing GST.
    const cashbackAmount = appliedCashback || 0;
    const finalSubtotal = Math.max(0, subtotalBeforeCashback - cashbackAmount);
    const finalCgst = totalCgstBeforeCashback;
    const finalSgst = totalSgstBeforeCashback;

    const totalSavings = programProductDiscount + cashbackAmount;

    // Calculate shipping charges with GST (only for product invoices)
    let shippingSubtotal = 0;
    let shippingCgstAmount = 0;
    let shippingSgstAmount = 0;
    let shippingTotal = 0;
    
    if (invoiceType === 'PRODUCT' && shippingEnabled && shippingCharges) {
      const shippingAmount = parseFloat(shippingCharges) || 0;
      if (shippingAmount > 300) {
        setFormError('Shipping charges cannot exceed ₹300');
        return;
      }
      
      // Calculate shipping GST: shippingAmount = base + 18% GST
      // base = shippingAmount / 1.18
      shippingSubtotal = gstApplicable ? shippingAmount / 1.18 : shippingAmount;
      shippingCgstAmount = gstApplicable ? shippingSubtotal * 0.09 : 0;
      shippingSgstAmount = gstApplicable ? shippingSubtotal * 0.09 : 0;
      shippingTotal = shippingAmount;
    }

    const finalTotalAmount = finalSubtotal + finalCgst + finalSgst + shippingTotal;

    setGstCalculations({
      subtotal: finalSubtotal.toFixed(2),
      totalCgst: finalCgst.toFixed(2),
      totalSgst: finalSgst.toFixed(2),
      totalDiscount: totalSavings.toFixed(2),
      totalAmount: finalTotalAmount.toFixed(2),
      subtotalBeforeCashback: subtotalBeforeCashback.toFixed(2),
      programProductDiscount: programProductDiscount.toFixed(2),
      shippingSubtotal: shippingSubtotal.toFixed(2),
      shippingCgst: shippingCgstAmount.toFixed(2),
      shippingSgst: shippingSgstAmount.toFixed(2),
      shippingTotal: shippingTotal.toFixed(2)
    });
  };

  const addLineItem = () => {
    if (!currentLineItem.itemId || !currentLineItem.quantity) {
      setFormError('Please select an item and enter quantity');
      return;
    }

    const itemType = currentLineItem.itemType;
    const items = itemType === 'PRODUCT' ? products : programs;
    
    // Check if items array is loaded
    if (!items || items.length === 0) {
      setFormError(`${itemType === 'PRODUCT' ? 'Products' : 'Programs'} are not loaded yet. Please wait and try again.`);
      return;
    }
    
    const selectedItem = items.find(item => item.id === parseInt(currentLineItem.itemId));
    
    if (!selectedItem) {
      setFormError('Selected item not found');
      return;
    }

    let unitPrice;
    try {
      if (itemType === 'PRODUCT') {
        // For products, get the appropriate tiered price based on customer's product discount plan
        const productDiscountPlanCode = customer?.productDiscountPlan?.planCode;
        unitPrice = getProductPriceForPlan(selectedItem, productDiscountPlanCode);
      } else {
        unitPrice = selectedItem.programMrp;
      }
      
      // Validate unit price
      if (!unitPrice || isNaN(unitPrice) || unitPrice <= 0) {
        setFormError('Invalid price for selected item. Please check the item configuration.');
        return;
      }
      
      const itemName = itemType === 'PRODUCT' ? selectedItem.productName : selectedItem.programName;
      const itemCode = itemType === 'PRODUCT' ? selectedItem.stockNo : selectedItem.programCode;

      const lineItem = {
        itemType: currentLineItem.itemType,
        itemId: parseInt(currentLineItem.itemId),
        itemName: itemName,
        itemCode: itemCode,
        quantity: parseInt(currentLineItem.quantity),
        unitPrice: unitPrice
      };

      setNewInvoice({
        ...newInvoice,
        lineItems: [...newInvoice.lineItems, lineItem]
      });

      // Reset current line item
      setCurrentLineItem({
        itemType: invoiceType,
        itemId: '',
        quantity: 1
      });
      setFormError('');
    } catch (error) {
      console.error('Error adding line item:', error);
      setFormError('Failed to add item. Please try again.');
    }
  };

  const removeLineItem = (index) => {
    const updatedLineItems = newInvoice.lineItems.filter((_, i) => i !== index);
    setNewInvoice({
      ...newInvoice,
      lineItems: updatedLineItems
    });
  };

  const fetchHealthReadings = async () => {
    try {
      setHealthReadingsLoading(true);
      const pageSize = 100;
      const firstPageResponse = await axios.get(`/api/customers/${customerId}/health-readings?page=0&size=${pageSize}`);
      const totalPages = firstPageResponse.data.totalPages || 1;
      let allReadings = [...(firstPageResponse.data.content || [])];

      if (totalPages > 1) {
        const remainingPageResponses = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, index) => (
            axios.get(`/api/customers/${customerId}/health-readings?page=${index + 1}&size=${pageSize}`)
          ))
        );

        remainingPageResponses.forEach(response => {
          allReadings = [...allReadings, ...(response.data.content || [])];
        });
      }

      const sortedReadings = allReadings.sort((first, second) => {
        const dateComparison = compareDateOnlyDesc(first.readingDate, second.readingDate);
        if (dateComparison !== 0) {
          return dateComparison;
        }

        return (second.id || 0) - (first.id || 0);
      });
      setHealthReadings(sortedReadings);
    } catch (error) {
      console.error('Failed to fetch health readings:', error);
    } finally {
      setHealthReadingsLoading(false);
    }
  };

  const fetchHealthReports = async () => {
    try {
      setHealthReportsLoading(true);
      const response = await axios.get(`/api/customers/${customerId}/health-reports`);
      setHealthReports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch health reports:', error);
    } finally {
      setHealthReportsLoading(false);
    }
  };

  const fetchUserPrograms = async () => {
    try {
      setUserProgramsLoading(true);
      const response = await axios.get(`/api/user-programs/${customerId}`);
      setUserPrograms(response.data || []);
    } catch (error) {
      console.error('Failed to fetch user programs:', error);
    } finally {
      setUserProgramsLoading(false);
    }
  };

  const handleAssignProgram = async (e) => {
    e.preventDefault();
    setAssignProgramLoading(true);
    setAssignProgramError('');

    try {
      await axios.post(`/api/user-programs/${customerId}/assign`, assignProgramData);
      
      trackEvent('program_assigned', {
        event_category: 'Program Management',
        event_label: 'Program Assigned',
        customer_id: customerId,
        program_id: assignProgramData.programId
      });
      
      setShowAssignProgramModal(false);
      setAssignProgramData({
        programId: '',
        startDate: getCurrentDateForInput(),
        endDate: '',
        comments: ''
      });
      fetchUserPrograms();
      setError('Program assigned successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to assign program:', error);
      setAssignProgramError(error.response?.data?.message || 'Failed to assign program. Please try again.');
    } finally {
      setAssignProgramLoading(false);
    }
  };

  const handleOpenAssignProgramModal = async () => {
    setShowAssignProgramModal(true);
    // Fetch programs if not already loaded
    if (programs.length === 0) {
      try {
        const response = await axios.get('/api/products/programs');
        setPrograms(response.data);
      } catch (error) {
        console.error('Failed to fetch programs:', error);
        setAssignProgramError('Failed to load programs. Please try again.');
      }
    }
  };

  const handleStopProgram = async () => {
    if (!selectedProgramToStop) return;
    
    setStopProgramLoading(true);
    try {
      await axios.post(`/api/user-programs/${selectedProgramToStop.id}/stop`, {
        closeComments: stopProgramComments
      });
      
      trackEvent('program_stopped', {
        event_category: 'Program Management',
        event_label: 'Program Stopped',
        customer_id: customerId,
        program_id: selectedProgramToStop.id
      });
      
      setShowStopProgramModal(false);
      setSelectedProgramToStop(null);
      setStopProgramComments('');
      fetchUserPrograms();
      setError('Program stopped successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to stop program:', error);
      setError('Failed to stop program. Please try again.');
    } finally {
      setStopProgramLoading(false);
    }
  };

  const handleSaveProgramDates = async () => {
    if (programDetailsSaving) return;
    setProgramDetailsSaving(true); setProgramDateMessage('');
    try {
      const {data} = await axios.patch(`/api/user-programs/${selectedProgramForDetails.id}/dates`, programDates);
      setSelectedProgramForDetails(p => ({...p,...data}));
      await fetchUserPrograms(); setProgramDateMessage('Program dates saved.');
    } catch (e) { setProgramDateMessage(e.response?.data?.message || e.response?.data || 'Could not update program dates.'); }
    finally { setProgramDetailsSaving(false); }
  };

  const handleSaveProgramComments = async () => {
    if (!selectedProgramForDetails) return;

    setProgramDetailsSaving(true);
    try {
      const response = await axios.patch(`/api/user-programs/${selectedProgramForDetails.id}/comments`, {
        comments: programDetailsComments
      });

      const updatedProgram = response.data;
      setSelectedProgramForDetails(updatedProgram);
      setUserPrograms(userPrograms.map(program =>
        program.id === updatedProgram.id ? updatedProgram : program
      ));
      setError('Program comments saved successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to update program comments:', error);
      setError('Failed to save program comments. Please try again.');
    } finally {
      setProgramDetailsSaving(false);
    }
  };

  const handleDeleteProgram = async (userProgramId) => {
    if (!window.confirm('Delete this program assignment? It will be removed from the customer’s programs. Existing attendance and issued invoices will be retained. Any unissued renewal draft for this assignment will be cancelled.')) {
      return;
    }

    try {
      await axios.delete(`/api/user-programs/${userProgramId}`);
      
      trackEvent('program_deleted', {
        event_category: 'Program Management',
        event_label: 'Program Deleted',
        customer_id: customerId,
        program_id: userProgramId
      });
      
      fetchUserPrograms();
      setError('Program deleted successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to delete program:', error);
      setError(error.response?.data?.message || (typeof error.response?.data === 'string' ? error.response.data : 'Failed to delete program. Please try again.'));
    }
  };

  const handleAddReading = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    try {
      const readingData = {
        ...newReading,
        weight: newReading.weight ? parseFloat(newReading.weight) : null,
        visceralFat: newReading.visceralFat ? parseFloat(newReading.visceralFat) : null,
        tsf: newReading.tsf ? parseFloat(newReading.tsf) : null,
        totalFatPercentage: newReading.totalFatPercentage ? parseFloat(newReading.totalFatPercentage) : null,
        metaAge: newReading.metaAge ? parseInt(newReading.metaAge) : null,
        bmi: newReading.bmi ? parseFloat(newReading.bmi) : null,
        bmr: newReading.bmr ? parseFloat(newReading.bmr) : null,
        skeletalMusclePercentage: newReading.skeletalMusclePercentage ? parseFloat(newReading.skeletalMusclePercentage) : null
      };

      if (editingReading) {
        await axios.put(`/api/customers/health-readings/${editingReading.id}`, readingData);
        trackEvent('health_reading_updated', {
          event_category: 'Health Management',
          event_label: 'Health Reading Updated',
          customer_id: customerId
        });
      } else {
        await axios.post(`/api/customers/${customerId}/health-readings`, readingData);
        trackEvent('health_reading_added', {
          event_category: 'Health Management',
          event_label: 'Health Reading Added',
          customer_id: customerId
        });
      }

      trackFormSubmission(editingReading ? 'edit_health_reading_form' : 'add_health_reading_form', true);
      setShowAddReading(false);
      setEditingReading(null);
      setNewReading({
        readingDate: getCurrentDateForInput(),
        weight: '',
        visceralFat: '',
        tsf: '',
        totalFatPercentage: '',
        metaAge: '',
        bmi: '',
        bmr: '',
        skeletalMusclePercentage: ''
      });
      await fetchHealthReadings();
      await fetchCustomerDetails();
    } catch (error) {
      console.error('Failed to save health reading:', error);
      setFormError(error.response?.data?.message || 'Failed to save health reading. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleEditReading = (reading) => {
    setEditingReading(reading);
    setNewReading({
      readingDate: reading.readingDate,
      weight: reading.weight || '',
      visceralFat: reading.visceralFat || '',
      tsf: reading.tsf || '',
      totalFatPercentage: reading.totalFatPercentage || '',
      metaAge: reading.metaAge || '',
      bmi: reading.bmi || '',
      bmr: reading.bmr || '',
      skeletalMusclePercentage: reading.skeletalMusclePercentage || ''
    });
    setShowAddReading(true);
  };

  const handleDeleteReading = async (readingId) => {
    if (!window.confirm('Are you sure you want to delete this health reading?')) {
      return;
    }

    try {
      await axios.delete(`/api/customers/health-readings/${readingId}`);
      await fetchHealthReadings();
      await fetchCustomerDetails();
    } catch (error) {
      console.error('Failed to delete health reading:', error);
      setError('Failed to delete health reading. Please try again.');
    }
  };

  const handleGenerateHealthReport = async () => {
    setGenerateReportLoading(true);
    setGenerateReportError('');
    setShowGenerateReportModal(true);
    
    // Show random messages during generation
    const messageInterval = setInterval(() => {
      const randomMessage = reportGenerationMessages[Math.floor(Math.random() * reportGenerationMessages.length)];
      setCurrentMessage(randomMessage);
    }, 2000);

    try {
      const response = await axios.post(`/api/customers/${customerId}/generate-health-report`, {}, {
        timeout: 0 // No timeout for LLM API calls
      });
      
      // Track health report generation
      trackEvent('health_report_generated', {
        event_category: 'Health Management',
        event_label: 'AI Health Report Generated',
        customer_id: customerId,
        report_id: response.data.reportId
      });
      
      clearInterval(messageInterval);
      setShowGenerateReportModal(false);
      
      // Show success message
      alert('Health report generated successfully! WhatsApp notification sent to customer.');
      
      // Refresh health reports list
      if (activeTab === 'reports') {
        fetchHealthReports();
      }
    } catch (error) {
      clearInterval(messageInterval);
      console.error('Failed to generate health report:', error);
      setGenerateReportError(error.response?.data?.message || 'Failed to generate health report. Please try again.');
    } finally {
      setGenerateReportLoading(false);
    }
  };

  const handleDownloadHealthReport = async (reportId, fileName) => {
    try {
      trackDownload(fileName, 'pdf');
      trackEvent('health_report_downloaded', {
        event_category: 'Health Management',
        event_label: fileName,
        report_id: reportId,
        customer_id: customerId
      });

      const response = await axios.get(`/api/customers/health-reports/${reportId}/download`, {
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download health report:', error);
      setError('Failed to download health report PDF. Please try again.');
    }
  };

  const handleDeleteHealthReport = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this health report?')) {
      return;
    }

    try {
      await axios.delete(`/api/customers/health-reports/${reportId}`);
      fetchHealthReports();
    } catch (error) {
      console.error('Failed to delete health report:', error);
      setError('Failed to delete health report. Please try again.');
    }
  };

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const response = await axios.get(`/api/customers/${customerId}/invoices?page=0&size=100`);
      setInvoices(response.data.content || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setInvoicesLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const getInvoiceStatusColor = (status) => {
    switch (status) {
      case 'PAID': return 'var(--success)';
      case 'PENDING': return 'var(--warning)';
      case 'OVERDUE': return 'var(--error)';
      case 'CANCELLED': return 'var(--gray-dark)';
      default: return 'var(--gray-dark)';
    }
  };

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  const parseRangeValue = (value) => {
    if (!value) return null;
    if (String(value).includes('-')) {
      const [min, max] = String(value).split('-').map(parseNumericValue);
      if (min === null || max === null) return null;
      return { min, max };
    }
    if (String(value).startsWith('<')) {
      const max = parseNumericValue(String(value).slice(1));
      if (max === null) return null;
      return { maxExclusive: max };
    }
    const exact = parseNumericValue(value);
    if (exact === null) return null;
    return { exact };
  };

  const getHealthValueColor = (value, rule) => {
    const numericValue = parseNumericValue(value);
    if (numericValue === null || !rule) {
      return 'inherit';
    }

    if (rule.maxExclusive !== undefined) {
      if (numericValue === rule.maxExclusive) return 'var(--warning)';
      return numericValue > rule.maxExclusive ? 'var(--error)' : 'var(--success)';
    }

    if (rule.min !== undefined && rule.max !== undefined) {
      if (numericValue < rule.min || numericValue > rule.max) return 'var(--error)';
      if (numericValue === rule.min || numericValue === rule.max) return 'var(--warning)';
      return 'var(--success)';
    }

    if (rule.exact !== undefined) {
      if (numericValue === rule.exact) return 'var(--warning)';
      return numericValue > rule.exact ? 'var(--error)' : 'var(--success)';
    }

    return 'inherit';
  };

  const getHealthReadingCellStyle = (value, rule) => ({
    padding: '12px',
    fontWeight: '500',
    color: getHealthValueColor(value, rule)
  });

  const handleAddInvoice = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    if (newInvoice.lineItems.length === 0) {
      setFormError('Please add at least one line item to the invoice');
      setFormLoading(false);
      return;
    }

    try {
      const invoiceData = {
        invoiceDate: newInvoice.invoiceDate,
        dueDate: newInvoice.dueDate || null,
        description: newInvoice.description,
        status: newInvoice.status,
        paymentDate: newInvoice.paymentDate || null,
        paymentMode: newInvoice.paymentMode || null,
        paymentType: newInvoice.paymentType,
        paidAmount: newInvoice.paidAmount ? parseFloat(newInvoice.paidAmount) : null,
        notes: newInvoice.notes || null,
        cashbackAmount: appliedCashback > 0 ? appliedCashback : null,
        customDiscountAmount: (invoiceType === 'PROGRAM' && customDiscountEnabled && customDiscountAmount) ? 
          parseFloat(customDiscountAmount) : null,
        shippingCharges: (invoiceType === 'PRODUCT' && shippingEnabled && shippingCharges) ? 
          parseFloat(shippingCharges) : null,
        lineItems: newInvoice.lineItems.map(item => ({
          itemType: item.itemType,
          itemId: item.itemId,
          quantity: item.quantity
        }))
      };

      if (editingInvoice) {
        await axios.put(`/api/customers/invoices/${editingInvoice.id}`, invoiceData);
        trackEvent('invoice_updated', {
          event_category: 'Invoice Management',
          event_label: 'Invoice Updated',
          customer_id: customerId,
          invoice_amount: gstCalculations.totalAmount
        });
      } else {
        await axios.post(renewalDraftId
          ? `/api/renewal-drafts/${renewalDraftId}/customers/${customerId}/invoice`
          : `/api/customers/${customerId}/invoices`, invoiceData);
        trackEvent('invoice_generated', {
          event_category: 'Invoice Management',
          event_label: 'Invoice Generated',
          customer_id: customerId,
          invoice_type: invoiceType,
          invoice_amount: gstCalculations.totalAmount,
          line_items_count: invoiceData.lineItems.length
        });
      }

      trackFormSubmission(editingInvoice ? 'edit_invoice_form' : 'add_invoice_form', true);
      setShowAddInvoice(false);
      setEditingInvoice(null);
      resetInvoiceForm();
      fetchInvoices();
      fetchCashbackBalance(); // Refresh cashback balance after invoice creation
    } catch (error) {
      console.error('Failed to save invoice:', error);
      setFormError(error.response?.data?.message || 'Failed to save invoice. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const resetInvoiceForm = () => {
    setRenewalDraftId(null);
    setRenewalDraftCompany(null);
    setNewInvoice({
      invoiceDate: getCurrentDateForInput(),
      dueDate: '',
      description: '',
      status: 'PAID',
      paymentDate: '',
      paymentMode: '',
      paymentType: 'FULL',
      paidAmount: '',
      notes: '',
      lineItems: []
    });
    setCurrentLineItem({
      itemType: 'PROGRAM',
      itemId: '',
      quantity: 1
    });
    setInvoiceType('PROGRAM');
    setAppliedCashback(0);
    setCustomDiscountEnabled(false);
    setCustomDiscountAmount('');
    setShippingEnabled(false);
    setShippingCharges('');
    setGstCalculations({
      subtotal: 0,
      totalCgst: 0,
      totalSgst: 0,
      totalDiscount: 0,
      totalAmount: 0,
      subtotalBeforeCashback: 0,
      programProductDiscount: 0,
      shippingSubtotal: 0,
      shippingCgst: 0,
      shippingSgst: 0,
      shippingTotal: 0
    });
  };

  const handleEditInvoice = (invoice) => {
    setEditingInvoice(invoice);
    fetchCashbackBalance();
    setNewInvoice({
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate || '',
      description: invoice.description || '',
      status: invoice.status || 'PENDING',
      paymentDate: invoice.paymentDate || '',
      paymentMode: invoice.paymentMode || '',
      paymentType: invoice.paymentType || 'FULL',
      paidAmount: invoice.paidAmount || '',
      notes: invoice.notes || '',
      lineItems: invoice.lineItems || []
    });
    setShowAddInvoice(true);
  };

  const handleCancelInvoice = async (invoiceId) => {
    if (!window.confirm(t('Are you sure you want to cancel this invoice?'))) {
      return;
    }

    try {
      await axios.delete(`/api/customers/invoices/${invoiceId}`);
      fetchInvoices();
    } catch (error) {
      console.error('Failed to cancel invoice:', error);
      setError(t('Failed to cancel invoice. Please try again.'));
    }
  };

  const handleDownloadInvoice = async (invoiceId, fileName) => {
    try {
      // Track invoice download
      trackDownload(fileName, 'pdf');
      trackEvent('invoice_downloaded', {
        event_category: 'Invoice Management',
        event_label: fileName,
        invoice_id: invoiceId,
        customer_id: customerId
      });

      const response = await axios.get(`/api/customers/invoices/${invoiceId}/download`, {
        responseType: 'blob',
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download invoice:', error);
      setError('Failed to download invoice PDF. Please try again.');
    }
  };

  const handleReshareInvoice = async (invoice) => {
    try {
      trackEvent('invoice_reshared_whatsapp', {
        event_category: 'Invoice Management',
        event_label: invoice.invoiceNumber,
        invoice_id: invoice.id,
        customer_id: customerId
      });

      await axios.post(`/api/customers/invoices/${invoice.id}/reshare-whatsapp`);
      window.alert(t('Invoice reshared successfully over WhatsApp.'));
    } catch (error) {
      console.error('Failed to reshare invoice over WhatsApp:', error);
      setError(error.response?.data || t('Failed to reshare invoice over WhatsApp. Please try again.'));
    }
  };

  const handleAddPartialPayment = (invoice) => {
    setSelectedInvoiceForPayment(invoice);
    setShowPartialPaymentModal(true);
  };

  const handlePartialPaymentAdded = (paymentData) => {
    // Refresh invoices list
    fetchInvoices();
    setShowPartialPaymentModal(false);
    setSelectedInvoiceForPayment(null);
    setError('Partial payment added successfully!');
    setTimeout(() => setError(''), 3000);
  };

  const handleApplyCashback = (amount) => {
    setAppliedCashback(amount);
    setFormError(''); // Clear any previous errors
  };

  const handleViewProgramAttendance = async (userProgram) => {
    setSelectedProgramAttendance(userProgram);
    setShowAttendanceModal(true);
    setAttendanceLoading(true);
    
    try {
      const response = await axios.get(`/api/user-programs/${userProgram.id}/attendance`);
      setAttendanceDates(response.data);
    } catch (error) {
      console.error('Failed to fetch program attendance:', error);
      setError('Failed to load attendance data. Please try again.');
    } finally {
      setAttendanceLoading(false);
    }
  };

  const fetchMealPlans = async () => {
    try {
      setMealPlansLoading(true);
      const response = await axios.get(`/api/customer-meal-plans/customer/${customerId}`);
      setMealPlans(response.data || []);
    } catch (error) {
      console.error('Failed to fetch meal plans:', error);
    } finally {
      setMealPlansLoading(false);
    }
  };

  const fetchRecognition = async () => {
    try {
      setBadgesLoading(true);
      const [definitions, assigned] = await Promise.all([
        axios.get('/api/badges'), axios.get(`/api/badges/customer/${customerId}`)
      ]);
      setAvailableBadges((definitions.data || []).filter(badge => badge.active));
      setCustomerBadges(assigned.data || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to load customer recognition.'));
    } finally { setBadgesLoading(false); }
  };

  const assignBadge = async () => {
    if (!selectedBadgeId) return;
    try {
      setBadgesLoading(true);
      await axios.post(`/api/badges/customer/${customerId}`, { badgeId: Number(selectedBadgeId) });
      setSelectedBadgeId('');
      await fetchRecognition();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, 'Failed to assign badge.'));
      setBadgesLoading(false);
    }
  };

  const removeBadge = async assignmentId => {
    if (!window.confirm('Remove this badge from the customer?')) return;
    try { await axios.delete(`/api/badges/customer/${customerId}/${assignmentId}`); await fetchRecognition(); }
    catch (requestError) { setError(getApiErrorMessage(requestError, 'Failed to remove badge.')); }
  };

  const fetchReferrals = async () => {
    try {
      setReferralsLoading(true);
      const response = await axios.get(`/api/customers/${customerId}/referrals`);
      setReferrals(response.data || []);
    } catch (error) {
      console.error('Failed to fetch referrals:', error);
    } finally {
      setReferralsLoading(false);
    }
  };

  const fetchFollowups = async () => {
    try {
      setFollowupsLoading(true);
      const response = await axios.get(`/api/customers/${customerId}/followups`);
      setFollowups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch followups:', error);
      setFollowups([]);
    } finally {
      setFollowupsLoading(false);
    }
  };

  const openAddFollowupModal = () => {
    const nextHour = new Date();
    nextHour.setHours(nextHour.getHours() + 1, 0, 0, 0);
    const localValue = new Date(nextHour.getTime() - nextHour.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setFollowupForm({
      summary: '',
      nextFollowupAt: localValue
    });
    setFollowupError('');
    setShowAddFollowupModal(true);
  };

  const handleAddFollowup = async (e) => {
    e.preventDefault();
    setFollowupSaving(true);
    setFollowupError('');

    try {
      await axios.post(`/api/customers/${customerId}/followups`, {
        summary: followupForm.summary,
        nextFollowupAt: followupForm.nextFollowupAt
      });
      setShowAddFollowupModal(false);
      setFollowupForm({ summary: '', nextFollowupAt: '' });
      await fetchFollowups();
    } catch (error) {
      console.error('Failed to add followup:', error);
      setFollowupError(getApiErrorMessage(error, 'Failed to save followup. Please try again.'));
    } finally {
      setFollowupSaving(false);
    }
  };

  const fetchWeightCategories = async () => {
    try {
      const response = await axios.get('/api/meal-plan-templates/weight-categories');
      setWeightCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch weight categories:', error);
    }
  };

  const fetchFoodPreferences = async () => {
    try {
      const response = await axios.get('/api/meal-plan-templates/food-preferences');
      setFoodPreferences(response.data || []);
    } catch (error) {
      console.error('Failed to fetch food preferences:', error);
    }
  };

  const fetchTemplatesByCriteria = async (weightCategory, foodPreference) => {
    try {
      const response = await axios.get('/api/meal-plan-templates/by-criteria', {
        params: { weightCategory, foodPreference }
      });
      setAvailableTemplates(response.data || []);
      
      // Auto-select first template if available
      if (response.data && response.data.length > 0) {
        const template = response.data[0];
        setSelectedTemplate(template);
        // Create a completely new array of items based on template (not referencing template items)
        const newMealPlanItems = (template.items || []).map((item, index) => ({
          timeSlot: item.timeSlot || '',
          description: item.description || '',
          notes: item.notes || '',
          sortOrder: item.sortOrder || (index + 1)
        }));
          
        setMealPlanData({
          mealPlanName: `${customer?.fullName} - ${template.templateName}`,
          weightCategory: weightCategory,
          foodPreference: foodPreference,
          instructions: template.instructions || '',
          items: newMealPlanItems
        });
      } else {
        // Clear meal plan data if no templates found
        setMealPlanData({
          mealPlanName: '',
          weightCategory: weightCategory,
          foodPreference: foodPreference,
          instructions: '',
          items: []
        });
      }
    } catch (error) {
      console.error('Failed to fetch templates by criteria:', error);
      setMealPlanError('Failed to load templates. Please try again.');
    }
  };

  const handleMealPlanCriteriaChange = (weightCategory, foodPreference) => {
    setSelectedWeightCategory(weightCategory);
    setSelectedFoodPreference(foodPreference);
    
    // Clear existing data when criteria changes
    setSelectedTemplate(null);
    setAvailableTemplates([]);
    setMealPlanData({
      mealPlanName: '',
      weightCategory: weightCategory,
      foodPreference: foodPreference,
      instructions: '',
      items: []
    });
    
    if (weightCategory && foodPreference) {
      fetchTemplatesByCriteria(weightCategory, foodPreference);
    }
  };

  const handleSaveMealPlan = async (e) => {
    e.preventDefault();
    setMealPlanLoading(true);
    setMealPlanError('');

    try {
      if (editingMealPlan) {
        await axios.put(`/api/customer-meal-plans/${editingMealPlan.id}`, mealPlanData);
      } else {
        await axios.post(`/api/customer-meal-plans/customer/${customerId}`, mealPlanData);
      }
      
      trackEvent(editingMealPlan ? 'meal_plan_updated' : 'meal_plan_created', {
        event_category: 'Meal Plan Management',
        event_label: editingMealPlan ? 'Meal Plan Updated' : 'Meal Plan Created',
        customer_id: customerId,
        meal_plan_id: editingMealPlan?.id,
        weight_category: mealPlanData.weightCategory,
        food_preference: mealPlanData.foodPreference
      });
      
      setShowAddMealPlan(false);
      resetMealPlanForm();
      fetchMealPlans();
      setError(editingMealPlan ? 'Meal plan updated successfully!' : 'Meal plan created successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to save meal plan:', error);
      setMealPlanError(error.response?.data?.message || 'Failed to save meal plan. Please try again.');
    } finally {
      setMealPlanLoading(false);
    }
  };

  const resetMealPlanForm = () => {
    setMealPlanData({
      mealPlanName: '',
      weightCategory: '',
      foodPreference: '',
      instructions: '',
      items: []
    });
    setSelectedWeightCategory('');
    setSelectedFoodPreference('');
    setSelectedTemplate(null);
    setEditingMealPlan(null);
    setAvailableTemplates([]);
    setMealPlanError('');
  };

  const handleEditMealPlan = async (mealPlan) => {
    resetMealPlanForm();
    setMealPlanLoading(true);
    setMealPlanError('');
    setShowAddMealPlan(true);

    try {
      const response = await axios.get(`/api/customer-meal-plans/${mealPlan.id}`);
      const mealPlanDetails = response.data;
      const editableItems = (mealPlanDetails.items || []).map((item, index) => ({
        id: item.id,
        timeSlot: item.timeSlot || '',
        description: item.description || '',
        notes: item.notes || '',
        sortOrder: item.sortOrder || (index + 1)
      }));

      setEditingMealPlan(mealPlanDetails);
      setSelectedWeightCategory(mealPlanDetails.weightCategory || '');
      setSelectedFoodPreference(mealPlanDetails.foodPreference || '');
      setSelectedTemplate({ id: `meal-plan-${mealPlanDetails.id}` });
      setAvailableTemplates([]);
      setMealPlanData({
        mealPlanName: mealPlanDetails.mealPlanName || '',
        weightCategory: mealPlanDetails.weightCategory || '',
        foodPreference: mealPlanDetails.foodPreference || '',
        instructions: mealPlanDetails.instructions || '',
        items: editableItems
      });
    } catch (error) {
      console.error('Failed to load meal plan for editing:', error);
      setMealPlanError(error.response?.data?.message || 'Failed to load meal plan. Please try again.');
    } finally {
      setMealPlanLoading(false);
    }
  };

  const updateMealPlanItem = (index, field, value) => {
    const updatedItems = [...mealPlanData.items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setMealPlanData({ ...mealPlanData, items: updatedItems });
  };

  const addMealPlanItem = () => {
    const newItem = {
      timeSlot: '',
      description: '',
      notes: '',
      sortOrder: mealPlanData.items.length + 1
    };
    setMealPlanData({
      ...mealPlanData,
      items: [...mealPlanData.items, newItem]
    });
  };

  const removeMealPlanItem = (index) => {
    const updatedItems = mealPlanData.items.filter((_, i) => i !== index);
    // Update sort order for remaining items
    const reorderedItems = updatedItems.map((item, i) => ({
      ...item,
      sortOrder: i + 1
    }));
    setMealPlanData({
      ...mealPlanData,
      items: reorderedItems
    });
  };

  const handleShareMealPlan = async (mealPlan) => {
    setSelectedMealPlanForShare(mealPlan);
    setShowShareModal(true);
  };

  const confirmShareMealPlan = async () => {
    try {
      await axios.post(`/api/customer-meal-plans/${selectedMealPlanForShare.id}/share`);
      
      trackEvent('meal_plan_shared', {
        event_category: 'Meal Plan Management',
        event_label: 'Meal Plan Shared via WhatsApp',
        customer_id: customerId,
        meal_plan_id: selectedMealPlanForShare.id
      });
      
      setShowShareModal(false);
      setSelectedMealPlanForShare(null);
      setError('Meal plan shared successfully via WhatsApp!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to share meal plan:', error);
      setError('Failed to share meal plan. Please try again.');
    }
  };

  const handleDownloadMealPlan = async (mealPlan) => {
    try {
      if (!mealPlan?.uniqueIdentifier) {
        throw new Error('Meal plan link is missing');
      }
      window.open(`/meal-plan/${mealPlan.uniqueIdentifier}`, '_blank', 'noopener,noreferrer');
      
      trackEvent('meal_plan_downloaded', {
        event_category: 'Meal Plan Management',
        event_label: mealPlan.mealPlanName,
        customer_id: customerId,
        meal_plan_id: mealPlan.id
      });
    } catch (error) {
      console.error('Failed to open meal plan:', error);
      setError('Failed to open meal plan. Please try again.');
    }
  };

  const handleDeleteMealPlan = async (mealPlanId) => {
    if (!window.confirm('Are you sure you want to delete this meal plan?')) {
      return;
    }

    try {
      await axios.delete(`/api/customer-meal-plans/${mealPlanId}`);
      fetchMealPlans();
      setError('Meal plan deleted successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to delete meal plan:', error);
      setError('Failed to delete meal plan. Please try again.');
    }
  };

  const validateEmail = async (email, excludeUserId = null) => {
    if (!email || !email.includes('@')) {
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setEmailValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { email };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }
      
      const response = await axios.get('/api/users/check-email', { params });
      
      if (response.data.exists) {
        setEmailValidation({
          isChecking: false,
          isValid: false,
          message: 'This email address is already registered'
        });
      } else {
        setEmailValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate email:', error);
      setEmailValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceEmailValidation = React.useCallback(
    debounce((email, excludeUserId) => validateEmail(email, excludeUserId), 500),
    []
  );

  const validateUsername = async (username, excludeUserId = null) => {
    if (!username || !username.trim()) {
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
      return;
    }

    setUsernameValidation({
      isChecking: true,
      isValid: true,
      message: ''
    });

    try {
      const params = { username: username.trim() };
      if (excludeUserId) {
        params.excludeUserId = excludeUserId;
      }

      const response = await axios.get('/api/users/check-username', { params });

      if (response.data.exists) {
        setUsernameValidation({
          isChecking: false,
          isValid: false,
          message: 'This username is already registered'
        });
      } else {
        setUsernameValidation({
          isChecking: false,
          isValid: true,
          message: ''
        });
      }
    } catch (error) {
      console.error('Failed to validate username:', error);
      setUsernameValidation({
        isChecking: false,
        isValid: true,
        message: ''
      });
    }
  };

  const debounceUsernameValidation = React.useCallback(
    debounce((username, excludeUserId) => validateUsername(username, excludeUserId), 500),
    []
  );

  const handleEditCustomer = async (e) => {
    e.preventDefault();
    
    if (!emailValidation.isValid) {
      setFormError('Please fix the email validation error before submitting.');
      return;
    }

    if (!usernameValidation.isValid) {
      setFormError('Please fix the username validation error before submitting.');
      return;
    }
    
    setFormLoading(true);
    setFormError('');

    try {
      await axios.put(`/api/customers/${customerId}`, editCustomerData);
      setShowEditCustomer(false);
      setFormError('');
      // Refresh customer details
      fetchCustomerDetails();
      setError('');
      // Show success message
      setError('Customer information updated successfully!');
      setTimeout(() => setError(''), 3000);
    } catch (error) {
      console.error('Failed to update customer:', error);
      setFormError(error.response?.data?.message || 'Failed to update customer. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handlePromoteToCoach = async () => {
    setPromoteLoading(true);
    setPromoteError('');

    try {
      await axios.post(`/api/promotion-requests/${customerId}`, null, {
        params: {
          reason: 'Customer promotion to coach role requested'
        }
      });
      
      setShowPromoteModal(false);
      
      // Show success confirmation
      alert('Promotion request sent successfully! You will be notified once the request is reviewed by an approver.');
      
    } catch (error) {
      console.error('Failed to create promotion request:', error);
      setPromoteError(error.response?.data?.message || 'Failed to send promotion request. Please try again.');
    } finally {
      setPromoteLoading(false);
    }
  };

  // Check if current user can promote this customer
  const canPromoteCustomer = () => {
    // Only SYSTEM_EDITOR can promote customers
    if (!user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')) {
      return false;
    }
    
    // Customer must have CUSTOMER member role
    if (!customer?.memberRole || customer.memberRole.code !== 'CUSTOMER') {
      return false;
    }
    
    // Additional check: customer should be a direct child (this will be validated by backend)
    return true;
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading">{t('Loading customer details...')}</div>
      </div>
    );
  }

  if (error && !customer) {
    return (
      <div className="page">
        <div className="alert alert-error">{error}</div>
        <button onClick={() => navigate('/customers')} className="btn btn-secondary">
          {t('Back to Customers')}
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <div>
          <button 
            onClick={() => navigate('/customers')} 
            className="btn btn-secondary"
            style={{ marginBottom: '10px' }}
          >
            ← {t('Back to Customers')}
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>
            {customer?.fullName || t('Customer Details')}
          </h1>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Customer Details Section - 20% of page */}
      <div className="card" style={{ marginBottom: '30px', minHeight: '20vh' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Customer Information')}</h3>
          <div style={{ display: 'flex', gap: '10px' }}>
            {canPromoteCustomer() && (
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPromoteModal(true)}
                style={{ 
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('Promote To Coach')}
              </button>
            )}
            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && customer?.memberRole?.code === 'CUSTOMER' && (
              <button 
                className="btn btn-secondary"
                onClick={() => setShowClubChangeModal(true)}
                style={{ 
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #EE5A52 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('Club Change')}
              </button>
            )}
            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowEditCustomer(true)}
              >
                {t('Edit Customer')}
              </button>
            )}
            {canManageCustomCashback() && (
              <button
                className="btn btn-secondary"
                onClick={openCustomCashbackModal}
                style={{
                  background: 'linear-gradient(135deg, #38B2AC 0%, #2C7A7B 100%)',
                  color: 'white',
                  border: 'none'
                }}
              >
                {t('Add Cashback')}
              </button>
            )}
          </div>
        </div>
        
        {customer && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Personal Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Email')}:</strong> {customer.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Phone')}:</strong> {customer.phone || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Gender')}:</strong> {customer.gender || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Address')}:</strong> {customer.address || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Date of Birth')}:</strong> {customer.dateOfBirth ? formatDateForDisplay(customer.dateOfBirth) : t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Age')}:</strong> {customer.dateOfBirth ? `${calculateAgeFromDateString(customer.dateOfBirth)} ${t('years')}` : t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Height')}:</strong> {customer.heightCm ? `${customer.heightCm} cm` : t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Total Weight Loss')}:</strong>{' '}
                {formatWeightLossValue(customer.totalWeightLoss) ? (
                  <span style={{
                    color: Number(customer.totalWeightLoss) > 0 ? 'var(--success)' : Number(customer.totalWeightLoss) < 0 ? 'var(--error)' : 'var(--gray-dark)',
                    fontWeight: '600'
                  }}>
                    {formatWeightLossValue(customer.totalWeightLoss)}
                  </span>
                ) : (
                  t('N/A')
                )}
              </div>
              {customer.discomfortsMedications && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>{t('Discomforts & Medications')}:</strong> {customer.discomfortsMedications}
                </div>
              )}
              {customer.gstNumber && (
                <div style={{ marginBottom: '8px' }}>
                  <strong>{t('GST Number')}:</strong> {customer.gstNumber}
                </div>
              )}
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Account Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Status')}:</strong> 
                <span style={{
                  color: getStatusColor(customer.status),
                  marginLeft: '5px',
                  fontWeight: '500'
                }}>
                  {customer.status}
                </span>
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('PC ID/Associate ID')}:</strong> {customer.pcId || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Customer Type')}:</strong> {customer.customerType || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Referred By')}:</strong> {customer.referredBy?.fullName || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Joined')}:</strong> {customer.joinedAt ? new Date(customer.joinedAt).toLocaleDateString() : t('N/A')}
              </div>
            </div>

            <div>
              <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Program Details')}</h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Active Program')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? activeProgram.programName : t('No Active Program');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Checkup Day')}:</strong> {customer.checkupDay || t('N/A')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Start Date')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.startDate) : t('N/A');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('End Date')}:</strong> {(() => {
                  const activeProgram = userPrograms.find(program => program.isActive);
                  return activeProgram ? formatDateForDisplay(activeProgram.endDate) : t('N/A');
                })()}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Program Discount')}:</strong> {customer.programDiscountPlan?.planName || t('Basic')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Product Discount')}:</strong> {customer.productDiscountPlan?.planName || t('Basic (MRP)')}
              </div>
            </div>
          </div>
        )}
        {isSystemAdmin() && customCashbackHistory.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <h4 style={{ color: 'var(--primary-blue)', marginBottom: '12px' }}>{t('Cashback Offered')}</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                    <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid var(--light-blue)' }}>{t('Amount')}</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Source')}</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Description')}</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Invoice')}</th>
                    <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created By')}</th>
                  </tr>
                </thead>
                <tbody>
                  {customCashbackHistory.map((cashback, index) => (
                    <tr key={cashback.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)' }}>
                      <td style={{ padding: '10px' }}>{cashback.createdAt ? formatDateForDisplay(cashback.createdAt) : '-'}</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>
                        ₹{Number(cashback.cashbackAmount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '10px' }}>{cashback.source || 'SYSTEM'}</td>
                      <td style={{ padding: '10px' }}>{cashback.description || cashback.programName || '-'}</td>
                      <td style={{ padding: '10px' }}>{cashback.invoiceNumber || '-'}</td>
                      <td style={{ padding: '10px' }}>{cashback.createdByName || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Tabbed Section - 80% of page */}
      <div className="card">
        {/* Tab Navigation */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          borderBottom: '2px solid var(--gray-medium)', 
          marginBottom: '20px' 
        }}>
          <button
            onClick={() => setActiveTab('health')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'health' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'health' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            {t('Health Readings')}
          </button>
          <button
            onClick={() => setActiveTab('invoices')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'invoices' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'invoices' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500'
            }}
          >
            {t('Invoices')}
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'reports' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'reports' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            {t('Health Reports')}
          </button>
          <button
            onClick={() => setActiveTab('programs')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'programs' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'programs' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginRight: '5px'
            }}
          >
            {t('Programs')}
          </button>
          <button
            onClick={() => setActiveTab('mealplan')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'mealplan' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'mealplan' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500'
            }}
          >
            {t('Meal Plan')}
          </button>
          <button
            onClick={() => setActiveTab('referals')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: activeTab === 'referals' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'referals' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer',
              borderRadius: '6px 6px 0 0',
              fontWeight: '500',
              marginLeft: '5px'
            }}
          >
            {t('Referrals')}
          </button>
          {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
            <button
              onClick={() => setActiveTab('recognition')}
              style={{ padding: '12px 24px', border: 'none', background: activeTab === 'recognition' ? 'var(--primary-blue)' : 'transparent', color: activeTab === 'recognition' ? 'var(--white)' : 'var(--gray-dark)', cursor: 'pointer', borderRadius: '6px 6px 0 0', fontWeight: '500', marginLeft: '5px' }}
            >Recognition</button>
          )}
          <button
            onClick={() => setActiveTab('settlements')}
            style={{
              padding: '12px 24px', border: 'none',
              background: activeTab === 'settlements' ? 'var(--primary-blue)' : 'transparent',
              color: activeTab === 'settlements' ? 'var(--white)' : 'var(--gray-dark)',
              cursor: 'pointer', borderRadius: '6px 6px 0 0', fontWeight: '500', marginLeft: '5px'
            }}
          >
            {t('Product Settlements')}
          </button>
          {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
            <button
              onClick={() => setActiveTab('followups')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: activeTab === 'followups' ? 'var(--primary-blue)' : 'transparent',
                color: activeTab === 'followups' ? 'var(--white)' : 'var(--gray-dark)',
                cursor: 'pointer',
                borderRadius: '6px 6px 0 0',
                fontWeight: '500',
                marginLeft: '5px'
              }}
            >
              {t('Followups')}
            </button>
          )}
        </div>

        {activeTab === 'settlements' && (
          <ProductSettlementsPanel
            customerId={customerId}
            canManage={user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR')}
            t={t}
          />
        )}

        {activeTab === 'recognition' && (
          <div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 22 }}>
              <select className="form-input" style={{ maxWidth: 420 }} value={selectedBadgeId} onChange={event => setSelectedBadgeId(event.target.value)}>
                <option value="">Select a badge to recognize {customer?.fullName || 'customer'}</option>
                {availableBadges.filter(badge => !customerBadges.some(assigned => assigned.id === badge.id)).map(badge => <option key={badge.id} value={badge.id}>{badge.name}</option>)}
              </select>
              <button className="btn btn-primary" disabled={!selectedBadgeId || badgesLoading} onClick={assignBadge}>Assign Badge</button>
            </div>
            {badgesLoading ? <div className="loading">Loading recognition...</div> : customerBadges.length === 0 ? <div style={{ textAlign: 'center', padding: 40, color: 'var(--gray-dark)' }}>No badges assigned yet.</div> : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 18 }}>
              {customerBadges.map(badge => <div key={badge.assignmentId} style={{ border: '1px solid var(--gray-medium)', borderRadius: 14, padding: 18, display: 'flex', gap: 16, alignItems: 'center' }}>
                <BadgeLogo badge={badge} size={135}/><div><h3 style={{ margin: '0 0 6px' }}>{badge.name}</h3><p style={{ color: 'var(--gray-dark)', fontSize: 13 }}>{badge.description}</p><small>Awarded {badge.awardedAt ? new Date(badge.awardedAt).toLocaleDateString() : ''}</small><div style={{ marginTop: 10 }}><button className="btn btn-secondary" onClick={() => removeBadge(badge.assignmentId)}>Remove</button></div></div>
              </div>)}
            </div>}
          </div>
        )}

        {/* Meal Plan Tab */}
        {activeTab === 'mealplan' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Customer Meal Plans')}</h3>
              {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    resetMealPlanForm();
                    setShowAddMealPlan(true);
                  }}
                >
                  {t('Add Meal Plan')}
                </button>
              )}
            </div>
            
            {mealPlansLoading ? (
              <div className="loading">{t('Loading meal plans...')}</div>
            ) : mealPlans.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No meal plans found. Click "Add Meal Plan" to create the first meal plan.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Meal Plan Name')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Weight Category')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Food Preference')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mealPlans.map((mealPlan, index) => (
                      <tr 
                        key={mealPlan.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {mealPlan.mealPlanName}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {mealPlan.weightCategory}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: mealPlan.foodPreference === 'VEG' ? 'var(--success)' : 'var(--error)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {mealPlan.foodPreference}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {new Date(mealPlan.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {mealPlan.uniqueIdentifier && (
                              <button
                                className="btn btn-primary"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                onClick={() => handleDownloadMealPlan(mealPlan)}
                                title={t('Open Meal Plan')}
                              >
                                📄
                              </button>
                            )}
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--success)', color: 'white', border: 'none' }}
                              onClick={() => handleShareMealPlan(mealPlan)}
                              title={t('Share via WhatsApp')}
                            >
                              📱
                            </button>
                            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                              <>
                                <button
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '4px 8px' }}
                                  onClick={() => handleEditMealPlan(mealPlan)}
                                  title={t('Edit Meal Plan')}
                                >
                                  ✏️
                                </button>
                                <button
                                  className="btn btn-secondary"
                                  style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                                  onClick={() => handleDeleteMealPlan(mealPlan.id)}
                                  title={t('Delete Meal Plan')}
                                >
                                  🗑️
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'referals' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('Customer Referrals')}</h3>
            {referralsLoading ? (
              <div className="loading">{t('Loading referrals...')}</div>
            ) : referrals.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No referrals found for this customer.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Name')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Email')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Phone')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Joined')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {referrals.map((referral, index) => (
                      <tr key={referral.id} style={{ backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)', borderBottom: '1px solid var(--gray-medium)' }}>
                        <td style={{ padding: '12px', fontWeight: '500' }}>{referral.fullName}</td>
                        <td style={{ padding: '12px' }}>{referral.email || t('N/A')}</td>
                        <td style={{ padding: '12px' }}>{referral.phone || t('N/A')}</td>
                        <td style={{ padding: '12px', color: getStatusColor(referral.status), fontWeight: '500' }}>{referral.status || t('N/A')}</td>
                        <td style={{ padding: '12px' }}>{referral.joinedAt ? new Date(referral.joinedAt).toLocaleDateString() : t('N/A')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Followups Tab */}
        {activeTab === 'followups' && user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Customer Followups')}</h3>
              <button className="btn btn-primary" onClick={openAddFollowupModal}>
                {t('Add New Followup')}
              </button>
            </div>

            {followupsLoading ? (
              <div className="loading">{t('Loading followups...')}</div>
            ) : followups.length === 0 ? (
              <div className="empty-state">
                <p>{t('No followups found. Add the first followup for this customer.')}</p>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--light-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--primary-blue)' }}>
                        {t('Discussion Summary')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--primary-blue)' }}>
                        {t('Next Followup')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--primary-blue)' }}>
                        {t('Added On')}
                      </th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--primary-blue)' }}>
                        {t('Coach')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {followups.map((followup, index) => (
                      <tr
                        key={followup.id}
                        style={{
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', whiteSpace: 'pre-wrap', maxWidth: '520px' }}>
                          {followup.summary}
                        </td>
                        <td style={{ padding: '12px', fontWeight: '600' }}>
                          {formatDateForDisplay(followup.nextFollowupAt)}
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                            {followup.nextFollowupAt ? new Date(followup.nextFollowupAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '-'}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(followup.createdAt)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {followup.coachName || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Health Readings Tab */}
        {activeTab === 'health' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Health Readings History')}</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowHealthGraph(true)}
                  disabled={healthReadingsLoading || healthReadings.length === 0}
                  title={healthReadings.length === 0 ? t('Add a health reading to view the graph') : t('View health progress graph')}
                >
                  📈 {t('Graph')}
                </button>
                {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                  <button className="btn btn-secondary" onClick={() => setShowLogMonitoring(true)}>
                    📊 {t('Log Monitoring')}
                  </button>
                )}
                {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                  <button 
                    className="btn btn-secondary"
                    onClick={handleGenerateHealthReport}
                    disabled={generateReportLoading}
                    style={{ 
                      background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    🤖 {t('Generate Health Report')}
                  </button>
                )}
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingReading(null);
                    setNewReading({
                      readingDate: new Date().toISOString().split('T')[0],
                      weight: '',
                      visceralFat: '',
                      tsf: '',
                      totalFatPercentage: '',
                      metaAge: '',
                      bmi: '',
                      bmr: '',
                      skeletalMusclePercentage: ''
                    });
                    setShowAddReading(true);
                  }}
                >
                  {t('Add Health Reading')}
                </button>
              </div>
            </div>

            {healthReadingsLoading ? (
              <div className="loading">{t('Loading health readings...')}</div>
            ) : healthReadings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No health readings found. Click "Add Health Reading" to add the first reading.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Weight (kg)')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Visceral Fat')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('TSF')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Total Fat (%)')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Meta Age')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('BMI')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('BMR')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Skeletal Muscle (%)')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                    {/* Ideal Values Sub-header */}
                    <tr style={{ backgroundColor: 'var(--light-blue)', fontSize: '12px' }}>
                      {(() => {
                        const resolvedIdealWeight = customer?.idealWeightKg || calculateIdealWeight(customer?.heightCm, customer?.gender);
                        const idealValues = getIdealHealthValues(customer?.gender, resolvedIdealWeight);

                        return (
                          <>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {t('Ideal Values')}:
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.weight || 'N/A'}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.visceralFat}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.tsf}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.totalFatPercentage}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.metaAge}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.bmi}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        -
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        {idealValues.skeletalMusclePercentage}
                      </th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', borderBottom: '1px solid var(--gray-medium)', fontWeight: 'bold', color: 'var(--dark-blue)' }}>
                        -
                      </th>
                          </>
                        );
                      })()}
                    </tr>
                  </thead>
                  <tbody>
                    {healthReadings.map((reading, index) => (
                      (() => {
                        const resolvedIdealWeight = customer?.idealWeightKg || calculateIdealWeight(customer?.heightCm, customer?.gender);
                        const idealValues = getIdealHealthValues(customer?.gender, resolvedIdealWeight);
                        const weightRule = resolvedIdealWeight ? { exact: parseNumericValue(resolvedIdealWeight) } : null;
                        const visceralFatRule = parseRangeValue(idealValues.visceralFat);
                        const tsfRule = parseRangeValue(idealValues.tsf);
                        const totalFatRule = parseRangeValue(idealValues.totalFatPercentage);
                        const bmiRule = parseRangeValue(idealValues.bmi);
                        const skeletalMuscleRule = parseRangeValue(idealValues.skeletalMusclePercentage);

                        return (
                      <tr 
                        key={reading.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(reading.readingDate)}
                        </td>
                        <td style={getHealthReadingCellStyle(reading.weight, weightRule)}>{reading.weight || '-'}</td>
                        <td style={getHealthReadingCellStyle(reading.visceralFat, visceralFatRule)}>{reading.visceralFat || '-'}</td>
                        <td style={getHealthReadingCellStyle(reading.tsf, tsfRule)}>{reading.tsf || '-'}</td>
                        <td style={getHealthReadingCellStyle(reading.totalFatPercentage, totalFatRule)}>{reading.totalFatPercentage || '-'}</td>
                        <td style={{ padding: '12px' }}>{reading.metaAge || '-'}</td>
                        <td style={getHealthReadingCellStyle(reading.bmi, bmiRule)}>{reading.bmi || '-'}</td>
                        <td style={{ padding: '12px' }}>{reading.bmr || '-'}</td>
                        <td style={getHealthReadingCellStyle(reading.skeletalMusclePercentage, skeletalMuscleRule)}>{reading.skeletalMusclePercentage || '-'}</td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px' }}
                              onClick={() => handleEditReading(reading)}
                              title={t('Edit Reading')}
                            >
                              ✏️
                            </button>
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                              onClick={() => handleDeleteReading(reading.id)}
                              title={t('Delete Reading')}
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                        );
                      })()
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === 'invoices' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Customer Invoices')}</h3>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  setEditingInvoice(null);
                  resetInvoiceForm();
                  fetchCashbackBalance();
                  setShowAddInvoice(true);
                }}
              >
                {t('Add Invoice')}
              </button>
            </div>
            
            {invoicesLoading ? (
              <div className="loading">{t('Loading invoices...')}</div>
            ) : invoices.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No invoices found. Click "Add Invoice" to create the first invoice.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Invoice #')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Description')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Total Amount')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Payment Info')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Due Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice, index) => (
                      <tr 
                        key={invoice.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {invoice.invoiceNumber}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(invoice.invoiceDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div>{invoice.description}</div>
                          {invoice.payments && invoice.payments.length > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--gray-dark)', marginTop: '4px' }}>
                              {t('{count} payment(s) made', { count: invoice.payments.length })}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          <div>₹{invoice.totalAmount || invoice.amount}</div>
                          {invoice.totalPaidAmount > 0 && (
                            <div style={{ fontSize: '11px', color: 'var(--success)' }}>
                              {t('Paid')}: ₹{invoice.totalPaidAmount}
                            </div>
                          )}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontSize: '12px' }}>
                            {invoice.payments && invoice.payments.length > 0 ? (
                              <div>
                                <div>{t('Total Paid')}: ₹{invoice.totalPaidAmount || 0}</div>
                                <div>{t('Balance')}: ₹{invoice.remainingBalance || 0}</div>
                                <div>{t('Last')}: {invoice.payments[0]?.paymentMode}</div>
                              </div>
                            ) : (
                              <div>
                                {invoice.paymentMode && <div>{t('Mode')}: {invoice.paymentMode}</div>}
                                {invoice.paymentType && <div>{t('Type')}: {invoice.paymentType}</div>}
                                {invoice.paidAmount && <div>{t('Paid')}: ₹{invoice.paidAmount}</div>}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            color: getInvoiceStatusColor(invoice.status),
                            fontWeight: '500'
                          }}>
                            {invoice.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            {invoice.hasPdf && (
                              <button
                                className="btn btn-primary"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                onClick={() => handleDownloadInvoice(invoice.id, invoice.pdfFileName || `invoice_${invoice.invoiceNumber}.pdf`)}
                                title={t('Download PDF')}
                              >
                                📄
                              </button>
                            )}
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px', background: '#25D366', color: 'white', border: 'none' }}
                              onClick={() => handleReshareInvoice(invoice)}
                              title={t('Reshare Invoice on WhatsApp')}
                            >
                              📲
                            </button>
                            {(invoice.status === 'PENDING' || invoice.status === 'OVERDUE') && (
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--success)', color: 'white', border: 'none' }}
                                onClick={() => handleAddPartialPayment(invoice)}
                                title={t('Add Partial Payment')}
                              >
                                💰
                              </button>
                            )}
                            {invoice.status !== 'CANCELLED' && (
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                onClick={() => handleEditInvoice(invoice)}
                                title={t('Edit Invoice')}
                              >
                                ✏️
                              </button>
                            )}
                            {invoice.status !== 'CANCELLED' && (
                              <button
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                                onClick={() => handleCancelInvoice(invoice.id)}
                                title={t('Cancel Invoice')}
                              >
                                ✖
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Health Reports Tab */}
        {activeTab === 'reports' && (
          <div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>{t('Health Reports')}</h3>
            
            {healthReportsLoading ? (
              <div className="loading">{t('Loading health reports...')}</div>
            ) : healthReports.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No health reports found. Generate your first AI-powered health report from the Health Readings tab.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Report Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Generated By')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('File Size')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Created')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {healthReports.map((report, index) => (
                      <tr 
                        key={report.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          {formatDateForDisplay(report.reportDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {report.generatedBy}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {report.pdfFileSize ? `${Math.round(report.pdfFileSize / 1024)} KB` : t('N/A')}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {new Date(report.createdAt).toLocaleDateString()}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                            {report.hasPdf && (
                              <button 
                                className="btn btn-primary"
                                style={{ fontSize: '11px', padding: '4px 8px' }}
                                onClick={() => handleDownloadHealthReport(report.id, report.pdfFileName || `health_report_${report.id}.pdf`)}
                                title={t('Download PDF')}
                              >
                                📄
                              </button>
                            )}
                            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                              <button 
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                                onClick={() => handleDeleteHealthReport(report.id)}
                                title={t('Delete Report')}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Assigned Programs')}</h3>
              {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                <button 
                  className="btn btn-primary"
                  onClick={handleOpenAssignProgramModal}
                >
                  {t('Assign Program')}
                </button>
              )}
            </div>
            
            {userProgramsLoading ? (
              <div className="loading">{t('Loading programs...')}</div>
            ) : userPrograms.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No programs assigned yet. Click "Assign Program" to assign the first program.')}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ 
                  width: '100%', 
                  borderCollapse: 'collapse',
                  fontSize: '14px'
                }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Program')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Start Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('End Date')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Volume Points')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Days Attended')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Status')}</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid var(--light-blue)' }}>{t('Assigned By')}</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid var(--light-blue)' }}>{t('Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPrograms.map((userProgram, index) => (
                      <tr 
                        key={userProgram.id}
                        style={{ 
                          backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                          borderBottom: '1px solid var(--gray-medium)'
                        }}
                      >
                        <td style={{ padding: '12px', fontWeight: '500' }}>
                          <div>{userProgram.programName}</div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {userProgram.programCode}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(userProgram.startDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          {formatDateForDisplay(userProgram.endDate)}
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '500' }}>
                            {userProgram.volumePointConsumption || 0} / {userProgram.totalVolumePoints || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {t('Consumption / Total')}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <div style={{ fontWeight: '500' }}>
                            {userProgram.daysAttended || 0}
                          </div>
                          <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                            {t('Days Present')}
                          </div>
                        </td>
                        <td style={{ padding: '12px' }}>
                          <span style={{
                            background: userProgram.isActive ? 'var(--success)' : 'var(--gray-medium)',
                            color: userProgram.isActive ? 'white' : 'var(--gray-dark)',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {userProgram.scheduled ? 'Scheduled' : userProgram.isActive ? t('Active') : t('Inactive')}
                          </span>
                        </td>
                        <td style={{ padding: '12px' }}>
                          {userProgram.assignedBy}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', gap: '5px', justifyContent: 'center', flexWrap: 'wrap' }}>
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--success)', color: 'white', border: 'none' }}
	                              onClick={() => {
	                                setSelectedProgramForDetails(userProgram);
	                                setProgramDetailsComments(userProgram.comments || '');
                                setProgramDates({startDate:userProgram.startDate,endDate:userProgram.endDate});
                                setProgramDateMessage('');
	                                setShowProgramDetailsModal(true);
	                              }}
                              title={t('View Program Details')}
                            >
                              👁️
                            </button>
                            <button 
                              className="btn btn-secondary"
                              style={{ fontSize: '11px', padding: '4px 8px', background: 'var(--primary-blue)', color: 'white', border: 'none' }}
                              onClick={() => handleViewProgramAttendance(userProgram)}
                              title={t('View Attendance')}
                            >
                              📅
                            </button>
                            {userProgram.isActive && user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                              <button 
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                                onClick={() => {
                                  setSelectedProgramToStop(userProgram);
                                  setShowStopProgramModal(true);
                                }}
                                title={t('Stop Program')}
                              >
                                {t('Stop')}
                              </button>
                            )}
                            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && (
                              <button 
                                className="btn btn-secondary"
                                style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                                onClick={() => handleDeleteProgram(userProgram.id)}
                                title={t('Delete Program')}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Followup Modal */}
      {showAddFollowupModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h3>{t('Add Followup')}</h3>
              <button
                type="button"
                className="close-btn"
                onClick={() => setShowAddFollowupModal(false)}
                disabled={followupSaving}
                aria-label={t('Close')}
              >
                ×
              </button>
            </div>

            <form className="profile-form" onSubmit={handleAddFollowup}>
              {followupError && <div className="alert alert-error">{followupError}</div>}

              <div className="form-group">
                <label htmlFor="followupSummary" className="form-label">
                  {t('Summary of Discussion')} *
                </label>
                <textarea
                  id="followupSummary"
                  className="form-input"
                  value={followupForm.summary}
                  onChange={(e) => setFollowupForm({ ...followupForm, summary: e.target.value })}
                  rows="4"
                  placeholder={t('Add consultation notes, customer concerns, plan discussed, or next action.')}
                  required
                  disabled={followupSaving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="nextFollowupAt" className="form-label">
                  {t('Next Followup Date & Time')} *
                </label>
                <input
                  id="nextFollowupAt"
                  type="datetime-local"
                  className="form-input"
                  value={followupForm.nextFollowupAt}
                  onChange={(e) => setFollowupForm({ ...followupForm, nextFollowupAt: e.target.value })}
                  required
                  disabled={followupSaving}
                />
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddFollowupModal(false)}
                  disabled={followupSaving}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={followupSaving || !followupForm.summary.trim() || !followupForm.nextFollowupAt}
                >
                  {followupSaving ? t('Saving...') : t('Save Followup')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Health Report Modal */}
      {showGenerateReportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '40px',
            width: '90%',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            {generateReportError ? (
              <div>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>❌</div>
                <h3 style={{ color: 'var(--error)', marginBottom: '20px' }}>
                  Report Generation Failed
                </h3>
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                  {generateReportError}
                </div>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowGenerateReportModal(false);
                    setGenerateReportError('');
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🤖</div>
                <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
                  Generating Health Report
                </h3>
                <div style={{
                  width: '60px',
                  height: '60px',
                  border: '4px solid var(--gray-medium)',
                  borderTop: '4px solid var(--primary-blue)',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px auto'
                }}></div>
                <p style={{ 
                  color: 'var(--gray-dark)', 
                  fontSize: '16px',
                  fontStyle: 'italic',
                  minHeight: '24px'
                }}>
                  {currentMessage}
                </p>
                <div style={{ 
                  fontSize: '12px', 
                  color: 'var(--gray-medium)',
                  marginTop: '20px'
                }}>
                  This may take a few moments while AI analyzes the health data...
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Health Reading Modal */}
      {showHealthGraph && (
        <HealthProgressGraphModal
          customer={customer}
          companyInfo={companyInfo}
          readings={healthReadings}
          onClose={() => setShowHealthGraph(false)}
        />
      )}
      {showLogMonitoring && customer && (
        <DailyLogMonitoringModal customer={customer} onClose={() => setShowLogMonitoring(false)} />
      )}

      {showAddReading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingReading ? 'Edit Health Reading' : 'Add Health Reading'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleAddReading}>
              <div className="form-group">
                <label htmlFor="readingDate" className="form-label">
                  Reading Date *
                </label>
                <input
                  type="date"
                  id="readingDate"
                  className="form-input"
                  value={newReading.readingDate}
                  onChange={(e) => setNewReading({...newReading, readingDate: e.target.value})}
                  required
                  disabled={formLoading}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="weight" className="form-label">
                    Weight (kg)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="weight"
                    className="form-input"
                    value={newReading.weight}
                    onChange={(e) => setNewReading({...newReading, weight: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="totalFatPercentage" className="form-label">
                    Total Fat (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="totalFatPercentage"
                    className="form-input"
                    value={newReading.totalFatPercentage}
                    onChange={(e) => setNewReading({...newReading, totalFatPercentage: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="visceralFat" className="form-label">
                    Visceral Fat
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="visceralFat"
                    className="form-input"
                    value={newReading.visceralFat}
                    onChange={(e) => setNewReading({...newReading, visceralFat: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="bmr" className="form-label">
                    BMR
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="bmr"
                    className="form-input"
                    value={newReading.bmr}
                    onChange={(e) => setNewReading({...newReading, bmr: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="bmi" className="form-label">
                    BMI
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="bmi"
                    className="form-input"
                    value={newReading.bmi}
                    onChange={(e) => setNewReading({...newReading, bmi: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="metaAge" className="form-label">
                    Meta Age
                  </label>
                  <input
                    type="number"
                    id="metaAge"
                    className="form-input"
                    value={newReading.metaAge}
                    onChange={(e) => setNewReading({...newReading, metaAge: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="tsf" className="form-label">
                    TSF
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="tsf"
                    className="form-input"
                    value={newReading.tsf}
                    onChange={(e) => setNewReading({...newReading, tsf: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="skeletalMusclePercentage" className="form-label">
                    Skeletal Muscle (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    id="skeletalMusclePercentage"
                    className="form-input"
                    value={newReading.skeletalMusclePercentage}
                    onChange={(e) => setNewReading({...newReading, skeletalMusclePercentage: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddReading(false);
                    setEditingReading(null);
                    setFormError('');
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading}
                >
                  {formLoading ? 'Saving...' : (editingReading ? 'Update Reading' : 'Add Reading')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Invoice Modal */}
      {showAddInvoice && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '95%',
            maxWidth: '1200px',
            maxHeight: '95vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingInvoice ? 'Edit Invoice' : 'Generate Invoice'}
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            {/* Company Information Section */}
            {companyInfo && (
              <div style={{
                border: '2px solid var(--light-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px',
                background: 'var(--pale-blue)'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Company Information')}</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '18px', marginBottom: '5px' }}>
                      {companyInfo.companyName}
                    </div>
                    <div>{companyInfo.addressLine1}</div>
                    {companyInfo.addressLine2 && <div>{companyInfo.addressLine2}</div>}
                    <div>{companyInfo.city}, {companyInfo.state} {companyInfo.pincode}</div>
                    <div>Phone: {companyInfo.phoneNumber}</div>
                    <div>Email: {companyInfo.email}</div>
                  </div>
                  <div>
                    <div><strong>{t('GSTIN')}:</strong> {companyInfo.gstinNumber || t('N/A')}</div>
                    <div><strong>{t('FSSAI')}:</strong> {companyInfo.fssaiNumber || t('N/A')}</div>
                    <div style={{ marginTop: '10px' }}>
                      <strong>{t('Invoice Date')}:</strong> {new Date().toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Customer Information Section */}
            <div style={{
              border: '2px solid var(--gray-medium)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px',
              background: 'var(--gray-light)'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Bill To')}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>
                    {customer?.fullName}
                  </div>
                  <div>{t('Email')}: {customer?.email}</div>
                  <div>{t('Phone')}: {customer?.phone}</div>
                  {customer?.address && <div>{t('Address')}: {customer.address}</div>}
                  {customer?.gstNumber && <div>{t('GST Number')}: {customer.gstNumber}</div>}
                </div>
                <div>
                  <div><strong>{t('Customer Type')}:</strong> {customer?.customerType}</div>
                  <div><strong>{t('Program')}:</strong> {customer?.program || t('N/A')}</div>
                  {customer?.discountPlan && (
                    <div><strong>{t('Discount Plan')}:</strong> {customer.discountPlan.planName}</div>
                  )}
                </div>
              </div>
            </div>

            <form onSubmit={handleAddInvoice}>
              {renewalDraftId && <p role="status">Renewal draft: review discounts and payment details below. Keep the assigned program with quantity one. The draft remains pending until this invoice is saved successfully.</p>}
              {/* Invoice Type Selection */}
              <div style={{
                border: '2px solid var(--primary-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Invoice Type')}</h4>
                <div style={{ display: 'flex', gap: '20px', marginBottom: '15px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="invoiceType"
                      value="PROGRAM"
                      checked={invoiceType === 'PROGRAM'}
                      onChange={(e) => {
                        if (newInvoice.lineItems.length > 0) {
                          alert(t('You have items in your invoice. Please generate the invoice first or remove all items before switching invoice type.'));
                          return;
                        }
                        setInvoiceType(e.target.value);
                        setCurrentLineItem({...currentLineItem, itemType: e.target.value});
                      }}
                      disabled={formLoading}
                    />
                    <span>{companyInfo?.gstinNumber?.trim() ? t('Program Invoice (18% GST)') : t('Program Invoice')}</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="radio"
                      name="invoiceType"
                      value="PRODUCT"
                      checked={invoiceType === 'PRODUCT'}
                      onChange={(e) => {
                        if (newInvoice.lineItems.length > 0) {
                          alert('You have items in your invoice. Please generate the invoice first or remove all items before switching invoice type.');
                          return;
                        }
                        setInvoiceType(e.target.value);
                        setCurrentLineItem({...currentLineItem, itemType: e.target.value});
                      }}
                      disabled={formLoading}
                    />
                    <span>{companyInfo?.gstinNumber?.trim() ? t('Product Invoice (5% GST)') : t('Product Invoice')}</span>
                  </label>
                </div>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                  {t('Note: You cannot mix products and programs in a single invoice')}
                </div>
                {!companyInfo?.gstinNumber?.trim() && (
                  <div style={{ marginTop: '10px', padding: '10px', borderRadius: '6px', background: 'var(--gray-light)', color: 'var(--gray-dark)' }}>
                    {t('GST will not be charged or shown because your company GSTIN is not configured.')}
                  </div>
                )}
                
                {/* Custom Discount Override for Program Invoices */}
                {invoiceType === 'PROGRAM' && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: 'var(--pale-blue)', 
                    borderRadius: '6px',
                    border: '1px solid var(--primary-blue)'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={customDiscountEnabled}
                        onChange={(e) => {
                          setCustomDiscountEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setCustomDiscountAmount('');
                          }
                        }}
                        disabled={formLoading}
                      />
                      <span style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                        {t('Override with Custom Discount')}
                      </span>
                    </label>
                    
                    {customDiscountEnabled && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label htmlFor="customDiscountAmount" className="form-label">
                          {t('Custom Discount Amount')} (₹) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          id="customDiscountAmount"
                          className="form-input"
                          value={customDiscountAmount}
                          onChange={(e) => setCustomDiscountAmount(e.target.value)}
                          required={customDiscountEnabled}
                          disabled={formLoading}
                          placeholder={t('Enter custom discount amount')}
                        />
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                          {t("This will override customer's program discount slab. Items will be shown at MRP.")}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Shipping Charges for Product Invoices */}
                {invoiceType === 'PRODUCT' && (
                  <div style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: 'var(--pale-blue)', 
                    borderRadius: '6px',
                    border: '1px solid var(--primary-blue)'
                  }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <input
                        type="checkbox"
                        checked={shippingEnabled}
                        onChange={(e) => {
                          setShippingEnabled(e.target.checked);
                          if (!e.target.checked) {
                            setShippingCharges('');
                          }
                        }}
                        disabled={formLoading}
                      />
                      <span style={{ fontWeight: '500', color: 'var(--dark-blue)' }}>
                        🚚 {t('Include Shipping Charges')}
                      </span>
                    </label>
                    
                    {shippingEnabled && (
                      <div className="form-group" style={{ margin: 0 }}>
                        <label htmlFor="shippingCharges" className="form-label">
                          {t('Shipping Charges')} (₹) *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="300"
                          id="shippingCharges"
                          className="form-input"
                          value={shippingCharges}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            if (value > 300) {
                              setFormError(t('Shipping charges cannot exceed ₹300'));
                              return;
                            }
                            setShippingCharges(e.target.value);
                            setFormError('');
                          }}
                          required={shippingEnabled}
                          disabled={formLoading}
                          placeholder={t('Enter shipping charges (max ₹300)')}
                        />
                        <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                          {companyInfo?.gstinNumber?.trim()
                            ? t('Maximum shipping charges allowed: ₹300. GST will be calculated at 18% (9% CGST + 9% SGST).')
                            : t('Maximum shipping charges allowed: ₹300. GST will not be charged.')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Line Items Section */}
              <div style={{
                border: '2px solid var(--gray-medium)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Add Line Items')}</h4>
                
                {/* Customer Discount Info */}
                <div style={{ 
                  marginBottom: '15px', 
                  padding: '10px', 
                  background: 'var(--pale-blue)', 
                  borderRadius: '6px',
                  fontSize: '14px'
                }}>
                  <div style={{ marginBottom: '5px' }}>
                    <strong>{t('Program Discount')}:</strong> {customer?.programDiscountPlan ?
                      customer.programDiscountPlan.planName :
                      t('Basic')
                    }
                  </div>
                  <div>
                    <strong>{t('Product Discount')}:</strong> {customer?.productDiscountPlan ?
                      `${customer.productDiscountPlan.planName}` :
                      t('Basic')
                    }
                  </div>
                </div>

                {/* Add Line Item Form */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '10px', alignItems: 'end', marginBottom: '15px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">
                      {invoiceType === 'PRODUCT' ? t('Product') : t('Program')} *
                    </label>
                    <SearchableDropdown
                      options={invoiceType === 'PRODUCT' ? products : programs.filter(p => p.programType === 'CLUB')}
                      value={currentLineItem.itemId}
                      onChange={(selectedId) => setCurrentLineItem({...currentLineItem, itemId: selectedId})}
                      disabled={formLoading}
                      required
                      valueKey="id"
                      displayKey={invoiceType === 'PRODUCT' ? 'productName' : 'programName'}
                      secondaryKey={invoiceType === 'PRODUCT' ? 'stockNo' : 'programCode'}
                      placeholder={t('Select {item}', { item: invoiceType === 'PRODUCT' ? t('Product') : t('Program') })}
                      searchPlaceholder={t('Search {items}...', { items: invoiceType === 'PRODUCT' ? t('products') : t('programs') })}
                      emptyMessage={t('No {items} available', { items: invoiceType === 'PRODUCT' ? t('products') : t('programs') })}
                      noResultsMessage={t('No {items} found', { items: invoiceType === 'PRODUCT' ? t('products') : t('programs') })}
                      searchKeys={
                        invoiceType === 'PRODUCT'
                          ? ['productName', 'stockNo', 'description']
                          : ['programName', 'programCode', 'description']
                      }
                      getOptionLabel={(item) => (
                        invoiceType === 'PRODUCT'
                          ? `${item.stockNo} - ${item.productName}`
                          : `${item.programCode} - ${item.programName}`
                      )}
                      getOptionSecondary={(item) => (
                        invoiceType === 'PRODUCT'
                          ? item.description || item.category || ''
                          : item.description || item.programType || ''
                      )}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">{t('Quantity')} *</label>
                    <input
                      type="number"
                      min="1"
                      className="form-input"
                      value={currentLineItem.quantity}
                      onChange={(e) => setCurrentLineItem({...currentLineItem, quantity: e.target.value})}
                      disabled={formLoading}
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">{t('Discount Applied')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={(() => {
                        if (invoiceType === 'PRODUCT') {
                          return customer?.productDiscountPlan ? `${customer.productDiscountPlan.planName}` : t('Basic (MRP)');
                        } else {
                          if (customDiscountEnabled) {
                            return t('Custom Discount Applied');
                          }
                          const selectedProgram = programs.find(p => p.id === parseInt(currentLineItem.itemId));
                          if (selectedProgram && !selectedProgram.discountApplicable) {
                            return t('Fixed Price (No Discount)');
                          }
                          return customer?.programDiscountPlan ? customer.programDiscountPlan.planName : t('Basic');
                        }
                      })()}
                      disabled={true}
                      style={{ backgroundColor: 'var(--gray-light)', color: 'var(--gray-dark)' }}
                    />
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={addLineItem}
                      disabled={formLoading || Boolean(renewalDraftId)}
                      style={{ padding: '8px 16px' }}
                    >
                      {t('Add Item')}
                    </button>
                  </div>
                </div>

                {/* Line Items Table */}
                {newInvoice.lineItems.length > 0 && (
                  <div style={{ marginTop: '20px' }}>
                    <h5 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>{t('Invoice Items')}</h5>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                        <thead>
                          <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                            <th style={{ padding: '8px', textAlign: 'left', border: '1px solid var(--gray-medium)' }}>{t('Item')}</th>
                            <th style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>{t('Qty')}</th>
                            <th style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>{t('MRP')}</th>
                            <th style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>{t('Discounted Price')}</th>
                            <th style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>{t('Total')}</th>
                            <th style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>{t('Action')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {newInvoice.lineItems.map((lineItem, index) => {
                            const {
                              mrpPrice,
                              discountedUnitPrice,
                              discountedTotal
                            } = getLineItemPricing(lineItem);
                            const lineTotal = customDiscountEnabled ? (parseFloat(lineItem.unitPrice) || 0) * (parseInt(lineItem.quantity) || 0) : discountedTotal;
                            
                            return (
                              <tr key={index}>
                                <td style={{ padding: '8px', border: '1px solid var(--gray-medium)' }}>
                                  <div style={{ fontWeight: 'bold' }}>{lineItem.itemName}</div>
                                  <div style={{ fontSize: '10px', color: 'var(--gray-dark)' }}>
                                    {lineItem.itemCode} • {lineItem.itemType}
                                  </div>
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>
                                  {lineItem.quantity}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>
                                  ₹{mrpPrice.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)' }}>
                                  ₹{(customDiscountEnabled ? (parseFloat(lineItem.unitPrice) || 0) : discountedUnitPrice).toFixed(2)}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'right', border: '1px solid var(--gray-medium)', fontWeight: 'bold' }}>
                                  ₹{lineTotal.toFixed(2)}
                                </td>
                                <td style={{ padding: '8px', textAlign: 'center', border: '1px solid var(--gray-medium)' }}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    style={{ fontSize: '10px', padding: '4px 8px', color: 'var(--error)' }}
                                    onClick={() => removeLineItem(index)}
                                    disabled={formLoading || Boolean(renewalDraftId)}
                                  >
                                    {t('Remove')}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Apply Cashback Section */}
                {newInvoice.lineItems.length > 0 && cashbackBalance && cashbackBalance.availableBalance > 0 && (
                  <div style={{ 
                    marginTop: '20px', 
                    padding: '15px',
                    background: appliedCashback > 0 ? 'linear-gradient(135deg, #C6F6D5 0%, #9AE6B4 100%)' : 'var(--pale-blue)',
                    borderRadius: '8px',
                    border: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div>
                        <div style={{ fontWeight: 'bold', color: 'var(--dark-blue)', marginBottom: '5px' }}>
                          💰 {t('Cashback Wallet')}
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--gray-dark)' }}>
                          {t('Available Balance')}: ₹{cashbackBalance.availableBalance.toFixed(2)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setShowCashbackModal(true)}
                          style={{ 
                            background: appliedCashback > 0 ? 'var(--warning)' : 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            fontSize: '14px'
                          }}
                        >
                          {appliedCashback > 0 ? `✏️ ${t('Modify Cashback')}` : `💰 ${t('Apply Cashback')}`}
                        </button>
                        {appliedCashback > 0 && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setAppliedCashback(0)}
                            style={{ 
                              background: 'var(--error)',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              fontSize: '12px',
                              marginLeft: '8px'
                            }}
                          >
                            ✕ {t('Remove')}
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {appliedCashback > 0 && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        padding: '12px',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: 'var(--dark-blue)',
                        border: '1px solid var(--success)'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{t('Cashback Applied')}:</span>
                          <span style={{ fontWeight: 'bold', fontSize: '16px', color: 'var(--success)' }}>
                            ₹{appliedCashback.toFixed(2)}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', marginTop: '5px', color: 'var(--gray-dark)' }}>
                          {t('This amount will be deducted from your cashback balance and applied as discount to the invoice.')}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* GST Calculations Display */}
                {newInvoice.lineItems.length > 0 && (
                  <div style={{
                    marginTop: '20px',
                    padding: '15px',
                    background: appliedCashback > 0 ? 'linear-gradient(135deg, #C6F6D5 0%, #9AE6B4 100%)' : 'var(--white)',
                    border: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`,
                    borderRadius: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h5 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Invoice Calculations')}</h5>
                      {appliedCashback > 0 && (
                        <div style={{
                          background: 'var(--success)',
                          color: 'white',
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px'
                        }}>
                          💰 {t('Cashback Applied')}: ₹{appliedCashback.toFixed(2)}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', fontSize: '14px' }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>{t('Subtotal (Before {type})', { type: customDiscountEnabled ? t('Custom Discount') : t('Cashback') })}:</span>
                          <span style={{ 
                            fontWeight: 'bold',
                            color: (appliedCashback > 0 || customDiscountEnabled) ? 'var(--gray-dark)' : 'inherit',
                            textDecoration: (appliedCashback > 0 || customDiscountEnabled) ? 'line-through' : 'none'
                          }}>
                            ₹{gstCalculations.subtotalBeforeCashback}
                          </span>
                        </div>
                        {customDiscountEnabled && customDiscountAmount && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ color: 'var(--warning)', fontWeight: '500' }}>{t('Custom Discount Applied')}:</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--warning)' }}>-₹{parseFloat(customDiscountAmount).toFixed(2)}</span>
                          </div>
                        )}
                        {appliedCashback > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                            <span style={{ color: 'var(--success)', fontWeight: '500' }}>{t('Cashback Applied')}:</span>
                            <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>-₹{appliedCashback.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>{t('Subtotal (After {type})', { type: customDiscountEnabled ? t('Custom Discount') : t('Cashback') })}:</span>
                          <span style={{ fontWeight: 'bold', color: (appliedCashback > 0 || customDiscountEnabled) ? 'var(--success)' : 'inherit' }}>₹{gstCalculations.subtotal}</span>
                        </div>
                        {companyInfo?.gstinNumber?.trim() && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span>{t('CGST ({rate}%)', { rate: invoiceType === 'PRODUCT' ? '2.5' : '9' })}:</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.totalCgst}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span>{t('SGST ({rate}%)', { rate: invoiceType === 'PRODUCT' ? '2.5' : '9' })}:</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.totalSgst}</span>
                            </div>
                          </>
                        )}
                        
                        {/* Shipping Charges Section */}
                        {invoiceType === 'PRODUCT' && shippingEnabled && parseFloat(gstCalculations.shippingTotal) > 0 && (
                          <>
                            <div style={{ 
                              borderTop: '1px solid var(--gray-medium)', 
                              marginTop: '10px', 
                              paddingTop: '10px',
                              fontSize: '13px',
                              color: 'var(--primary-blue)',
                              fontWeight: 'bold'
                            }}>
                              🚚 {t('Shipping Charges')}:
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                              <span>{companyInfo?.gstinNumber?.trim() ? t('Shipping (Base)') : t('Shipping Charges')}:</span>
                              <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingSubtotal}</span>
                            </div>
                            {companyInfo?.gstinNumber?.trim() && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                                  <span>{t('Shipping CGST (9%)')}:</span>
                                  <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingCgst}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px', fontSize: '12px' }}>
                                  <span>{t('Shipping SGST (9%)')}:</span>
                                  <span style={{ fontWeight: 'bold' }}>₹{gstCalculations.shippingSgst}</span>
                                </div>
                              </>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                              <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>{t('Total Shipping')}:</span>
                              <span style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>₹{gstCalculations.shippingTotal}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>{customDiscountEnabled ? `${t('Custom Discount')}:` : `${t('Program/Product Discount')}:`}</span>
                          <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>
                            ₹{gstCalculations.programProductDiscount || '0.00'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                          <span>{t('Total Savings')}:</span>
                          <span style={{ fontWeight: 'bold', color: 'var(--success)' }}>₹{gstCalculations.totalDiscount}</span>
                        </div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          marginTop: '10px',
                          paddingTop: '10px',
                          borderTop: `2px solid ${appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)'}`,
                          fontSize: '16px'
                        }}>
                          <span style={{ fontWeight: 'bold' }}>{t('Final Amount')}:</span>
                          <span style={{ 
                            fontWeight: 'bold', 
                            color: appliedCashback > 0 ? 'var(--success)' : 'var(--primary-blue)',
                            fontSize: '18px'
                          }}>
                            ₹{Math.round(parseFloat(gstCalculations.totalAmount))}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {appliedCashback > 0 && (
                      <div style={{
                        marginTop: '15px',
                        padding: '10px',
                        background: 'rgba(255, 255, 255, 0.8)',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: 'var(--dark-blue)',
                        textAlign: 'center'
                      }}>
                        {t("Great! You're saving ₹{amount} with cashback discount. GST has been recalculated on the reduced amount.", { amount: appliedCashback.toFixed(2) })}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Invoice Details */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="invoiceDate" className="form-label">
                    Invoice Date *
                  </label>
                  <input
                    type="date"
                    id="invoiceDate"
                    className="form-input"
                    value={newInvoice.invoiceDate}
                    onChange={(e) => setNewInvoice({...newInvoice, invoiceDate: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dueDate" className="form-label">
                    Due Date
                  </label>
                  <input
                    type="date"
                    id="dueDate"
                    className="form-input"
                    value={newInvoice.dueDate}
                    onChange={(e) => setNewInvoice({...newInvoice, dueDate: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label">
                  Description *
                </label>
                <input
                  type="text"
                  id="description"
                  className="form-input"
                  value={newInvoice.description}
                  onChange={(e) => setNewInvoice({...newInvoice, description: e.target.value})}
                  required
                  disabled={formLoading}
                  placeholder={t('e.g., Monthly program fee')}
                />
              </div>

              {/* Payment Information */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="paymentType" className="form-label">
                    {t('Payment Type')} *
                  </label>
                  <select
                    id="paymentType"
                    className="form-input"
                    value={newInvoice.paymentType}
                    onChange={(e) => {
                      const paymentType = e.target.value;
                      setNewInvoice({
                        ...newInvoice, 
                        paymentType: paymentType,
                        status: paymentType === 'FULL' ? 'PAID' : (paymentType === 'NONE' ? 'PENDING' : newInvoice.status)
                      });
                    }}
                    required
                    disabled={formLoading}
                  >
                    <option value="FULL">Full Payment</option>
                    <option value="PARTIAL">Partial Payment</option>
                    <option value="NONE">No Payment</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentMode" className="form-label">
                    {t('Payment Mode')}
                  </label>
                  <select
                    id="paymentMode"
                    className="form-input"
                    value={newInvoice.paymentMode}
                    onChange={(e) => setNewInvoice({...newInvoice, paymentMode: e.target.value})}
                    disabled={formLoading}
                  >
                    <option value="">Select Mode</option>
                    <option value="CASH">Cash</option>
                    <option value="CARD">Card</option>
                    <option value="UPI">UPI</option>
                    <option value="BANK_TRANSFER">Bank Transfer</option>
                    <option value="CHEQUE">Cheque</option>
                  </select>
                </div>

                {newInvoice.paymentType === 'PARTIAL' && (
                  <div className="form-group">
                    <label htmlFor="paidAmount" className="form-label">
                      Paid Amount (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      id="paidAmount"
                      className="form-input"
                      value={newInvoice.paidAmount}
                      onChange={(e) => setNewInvoice({...newInvoice, paidAmount: e.target.value})}
                      disabled={formLoading}
                      placeholder="0.00"
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status *
                  </label>
                  <select
                    id="status"
                    className="form-input"
                    value={newInvoice.status}
                    onChange={(e) => setNewInvoice({...newInvoice, status: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="PENDING">Pending</option>
                    <option value="PAID">Paid</option>
                    <option value="OVERDUE">Overdue</option>
                    {!renewalDraftId && <option value="CANCELLED">Cancelled</option>}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="paymentDate" className="form-label">
                    Payment Date
                  </label>
                  <input
                    type="date"
                    id="paymentDate"
                    className="form-input"
                    value={newInvoice.paymentDate}
                    onChange={(e) => setNewInvoice({...newInvoice, paymentDate: e.target.value})}
                    disabled={formLoading}
                  />
                </div>
              </div>


              <div className="form-group">
                <label htmlFor="notes" className="form-label">
                  Notes
                </label>
                <textarea
                  id="notes"
                  className="form-input"
                  rows="3"
                  value={newInvoice.notes}
                  onChange={(e) => setNewInvoice({...newInvoice, notes: e.target.value})}
                  disabled={formLoading}
                  placeholder={t('Additional notes...')}
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddInvoice(false);
                    setEditingInvoice(null);
                    setFormError('');
                    resetInvoiceForm();
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading || newInvoice.lineItems.length === 0}
                >
                  {formLoading ? 'Saving...' : (editingInvoice ? 'Update Invoice' : 'Generate Invoice')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Customer Modal */}
      {showEditCustomer && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '700px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              Edit Customer Information
            </h3>

            {formError && (
              <div className="alert alert-error">{formError}</div>
            )}

            <form onSubmit={handleEditCustomer}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="firstName" className="form-label">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    className="form-input"
                    maxLength="50"
                    value={editCustomerData.firstName}
                    onChange={(e) => setEditCustomerData({...editCustomerData, firstName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName" className="form-label">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    className="form-input"
                    maxLength="50"
                    value={editCustomerData.lastName}
                    onChange={(e) => setEditCustomerData({...editCustomerData, lastName: e.target.value})}
                    required
                    disabled={formLoading}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">
                    Email Address *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="email"
                      id="email"
                      className={`form-input ${!emailValidation.isValid ? 'error' : ''}`}
                      value={editCustomerData.email}
                      onChange={(e) => {
                        const email = e.target.value;
                        setEditCustomerData({...editCustomerData, email: email});
                        debounceEmailValidation(email, parseInt(customerId));
                      }}
                      required
                      disabled={formLoading}
                      style={{
                        paddingRight: emailValidation.isChecking ? '40px' : '10px',
                        borderColor: !emailValidation.isValid ? 'var(--error)' : undefined
                      }}
                    />
                    {emailValidation.isChecking && (
                      <div style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--primary-blue)'
                      }}>
                        ⏳
                      </div>
                    )}
                  </div>
                  {!emailValidation.isValid && emailValidation.message && (
                    <div style={{
                      color: 'var(--error)',
                      fontSize: '12px',
                      marginTop: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      ❌ {emailValidation.message}
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="username" className="form-label">
                    Username *
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      id="username"
                      className={`form-input ${!usernameValidation.isValid ? 'error' : ''}`}
                      value={editCustomerData.username}
                      onChange={(e) => {
                        const username = e.target.value;
                        setEditCustomerData({...editCustomerData, username});
                        debounceUsernameValidation(username, parseInt(customerId));
                      }}
                      required
                      disabled={formLoading}
                      placeholder="Enter username"
                      style={{
                        paddingRight: usernameValidation.isChecking ? '40px' : '10px',
                        borderColor: !usernameValidation.isValid ? 'var(--error)' : undefined
                      }}
                    />
                    {usernameValidation.isChecking && (
                      <div style={{
                        position: 'absolute',
                        right: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: 'var(--primary-blue)'
                      }}>
                        ⏳
                      </div>
                    )}
                  </div>
                  {!usernameValidation.isValid && usernameValidation.message && (
                    <div style={{
                      color: 'var(--error)',
                      fontSize: '12px',
                      marginTop: '5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      ❌ {usernameValidation.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  className="form-input"
                  value={editCustomerData.password}
                  onChange={(e) => setEditCustomerData({...editCustomerData, password: e.target.value})}
                  disabled={formLoading}
                  placeholder="Leave blank if you don't want to change"
                />
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  Leave blank if you don't want to change the password
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="gender" className="form-label">
                    Gender *
                  </label>
                  <select
                    id="gender"
                    className="form-input"
                    value={editCustomerData.gender}
                    onChange={(e) => {
                      const gender = e.target.value;
                      setEditCustomerData({...editCustomerData, gender: gender});
                      
                      // Auto-calculate ideal weight when gender changes
                      if (editCustomerData.heightCm && gender) {
                        const idealWeight = calculateIdealWeight(editCustomerData.heightCm, gender);
                        if (idealWeight) {
                          setEditCustomerData(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                        }
                      }
                    }}
                    required
                    disabled={formLoading}
                  >
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="status" className="form-label">
                    Status *
                  </label>
                  <select
                    id="status"
                    className="form-input"
                    value={editCustomerData.status}
                    onChange={(e) => setEditCustomerData({...editCustomerData, status: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="ARCHIVED">Archived</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="dateOfBirth" className="form-label">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      id="dateOfBirth"
                      className="form-input"
                      value={editCustomerData.dateOfBirth}
                      onChange={(e) => {
                        const dob = e.target.value;
                        setEditCustomerData({...editCustomerData, dateOfBirth: dob});
                      
                        // Auto-calculate age if DOB is provided
                        if (dob) {
                          const age = calculateAgeFromDateString(dob);
                          setEditCustomerData(prev => ({...prev, age: age ? age.toString() : ''}));
                        }
                      }}
                      disabled={formLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="age" className="form-label">
                      Age (years)
                    </label>
                    <input
                      type="number"
                      id="age"
                      className="form-input"
                      value={editCustomerData.age}
                      onChange={(e) => setEditCustomerData({...editCustomerData, age: e.target.value})}
                      disabled={formLoading}
                      placeholder="Auto-filled from DOB"
                      min="1"
                      max="120"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="heightCm" className="form-label">
                      Height (cm)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      id="heightCm"
                      className="form-input"
                      value={editCustomerData.heightCm}
                      onChange={(e) => {
                        const height = e.target.value;
                        setEditCustomerData({...editCustomerData, heightCm: height});
                        
                        // Auto-calculate ideal weight when height changes
                        if (height && editCustomerData.gender) {
                          const idealWeight = calculateIdealWeight(height, editCustomerData.gender);
                          if (idealWeight) {
                            setEditCustomerData(prev => ({...prev, idealWeightKg: idealWeight.toString()}));
                          }
                        }
                      }}
                      disabled={formLoading}
                      placeholder="e.g., 175.5"
                      min="50"
                      max="250"
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="initialWeightKg" className="form-label">
                      Initial Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      id="initialWeightKg"
                      className="form-input"
                      value={editCustomerData.initialWeightKg}
                      onChange={(e) => setEditCustomerData({...editCustomerData, initialWeightKg: e.target.value})}
                      disabled={formLoading}
                      placeholder="e.g., 85.5"
                      min="20"
                      max="300"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="idealWeightKg" className="form-label">
                      Ideal Weight (kg)
                    </label>
                    <input
                      type="number"
                      step="0.1"
                      id="idealWeightKg"
                      className="form-input"
                      value={editCustomerData.idealWeightKg}
                      onChange={(e) => setEditCustomerData({...editCustomerData, idealWeightKg: e.target.value})}
                      disabled={formLoading}
                      placeholder="e.g., 70.0"
                      min="20"
                      max="300"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number *
                  </label>
                  <CountryPhoneInput
                    phoneValue={editCustomerData.phone}
                    countryValue={editCustomerData.phoneCountryId}
                    onPhoneChange={(phone) => setEditCustomerData({...editCustomerData, phone})}
                    onCountryChange={(countryId) => setEditCustomerData({...editCustomerData, phoneCountryId: countryId})}
                    disabled={formLoading}
                    required={true}
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="address" className="form-label">
                  Address
                </label>
                <textarea
                  id="address"
                  className="form-input"
                  rows="3"
                  value={editCustomerData.address}
                  onChange={(e) => setEditCustomerData({...editCustomerData, address: e.target.value})}
                  disabled={formLoading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="gstNumber" className="form-label">
                  GST Number
                </label>
                <input
                  type="text"
                  id="gstNumber"
                  className="form-input"
                  value={editCustomerData.gstNumber}
                  onChange={(e) => setEditCustomerData({...editCustomerData, gstNumber: e.target.value})}
                  disabled={formLoading}
                  placeholder="e.g., 22AAAAA0000A1Z5"
                  maxLength="15"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="pcId" className="form-label">
                    PC ID/Associate ID
                  </label>
                  <input
                    type="text"
                    id="pcId"
                    className="form-input"
                    value={editCustomerData.pcId}
                    onChange={(e) => setEditCustomerData({...editCustomerData, pcId: e.target.value})}
                    disabled={formLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="customerType" className="form-label">
                    Customer Type *
                  </label>
                  <select
                    id="customerType"
                    className="form-input"
                    value={editCustomerData.customerType}
                    onChange={(e) => setEditCustomerData({...editCustomerData, customerType: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="ONLINE">Online</option>
                    <option value="CLUB">Club</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="checkupDay" className="form-label">
                    Checkup Day *
                  </label>
                  <select
                    id="checkupDay"
                    className="form-input"
                    value={editCustomerData.checkupDay}
                    onChange={(e) => setEditCustomerData({...editCustomerData, checkupDay: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="MONDAY">Monday</option>
                    <option value="TUESDAY">Tuesday</option>
                    <option value="WEDNESDAY">Wednesday</option>
                    <option value="THURSDAY">Thursday</option>
                    <option value="FRIDAY">Friday</option>
                    <option value="SATURDAY">Saturday</option>
                    <option value="SUNDAY">Sunday</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="program" className="form-label">
                    Program *
                  </label>
                  <select
                    id="program"
                    className="form-input"
                    value={editCustomerData.program}
                    onChange={(e) => setEditCustomerData({...editCustomerData, program: e.target.value})}
                    required
                    disabled={formLoading}
                  >
                    <option value="">Select Program</option>
                    {programOptions.map(program => (
                      <option key={program} value={program}>
                        {program}
                      </option>
                    ))}
                  </select>
                </div>
              </div>


              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="programDiscountSlab" className="form-label">
                    Program Discount Slab
                  </label>
                  <select
                    id="programDiscountSlab"
                    className="form-input"
                    value={editCustomerData.programDiscountSlabId}
                    onChange={(e) => setEditCustomerData({...editCustomerData, programDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    <option value="">Basic</option>
                    {discountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="productDiscountSlab" className="form-label">
                    Product Discount Slab
                  </label>
                  <select
                    id="productDiscountSlab"
                    className="form-input"
                    value={editCustomerData.productDiscountSlabId}
                    onChange={(e) => setEditCustomerData({...editCustomerData, productDiscountSlabId: e.target.value})}
                    disabled={formLoading}
                  >
                    {productDiscountPlans.map(plan => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="referredBy" className="form-label">
                  Referred By
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    id="referredBy"
                    className="form-input"
                    placeholder="Search and select referrer..."
                    value={editCustomerData.referredBySearch || ''}
                    onChange={(e) => {
                      const searchValue = e.target.value;
                      setEditCustomerData({...editCustomerData, referredBySearch: searchValue});
                      
                      // Filter options based on search and exclude current customer
                      const allOptions = [
                        { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                        ...referralOptions
                      ];
                      const filtered = allOptions.filter(option => 
                        option.id !== parseInt(customerId) && // Exclude current customer from referral options
                        (option.fullName.toLowerCase().includes(searchValue.toLowerCase()) ||
                        option.email.toLowerCase().includes(searchValue.toLowerCase()))
                      );
                      setEditCustomerData(prev => ({...prev, filteredReferralOptions: filtered}));
                    }}
                    onFocus={() => {
                      const allOptions = [
                        { id: user.id, fullName: `${user.fullName} (You)`, email: user.email },
                        ...referralOptions
                      ];
                      // Filter out current customer from referral options
                      const filteredOptions = allOptions.filter(option => option.id !== parseInt(customerId));
                      setEditCustomerData(prev => ({...prev, showReferralDropdown: true, filteredReferralOptions: filteredOptions}));
                    }}
                    disabled={formLoading}
                  />
                  
                  {editCustomerData.showReferralDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'var(--white)',
                      border: '1px solid var(--gray-medium)',
                      borderRadius: '4px',
                      maxHeight: '200px',
                      overflowY: 'auto',
                      zIndex: 1000,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                    }}>
                      {(editCustomerData.filteredReferralOptions || []).length === 0 ? (
                        <div style={{ padding: '10px', color: 'var(--gray-dark)' }}>
                          No referral options available
                        </div>
                      ) : (
                        (editCustomerData.filteredReferralOptions || []).map(option => (
                          <div
                            key={option.id}
                            style={{
                              padding: '10px',
                              cursor: 'pointer',
                              borderBottom: '1px solid var(--gray-light)'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--pale-blue)'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            onClick={() => {
                              setEditCustomerData({
                                ...editCustomerData,
                                referredById: option.id,
                                referredBySearch: option.fullName,
                                showReferralDropdown: false,
                                filteredReferralOptions: []
                              });
                            }}
                          >
                            <div style={{ fontWeight: '500' }}>{option.fullName}</div>
                            <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>{option.email}</div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="discomfortsMedications" className="form-label">
                  Discomforts & Medications
                </label>
                <textarea
                  id="discomfortsMedications"
                  className="form-input"
                  rows="4"
                  value={editCustomerData.discomfortsMedications}
                  onChange={(e) => setEditCustomerData({...editCustomerData, discomfortsMedications: e.target.value})}
                  disabled={formLoading}
                  placeholder="Enter any discomforts, medical conditions, or medications..."
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditCustomer(false);
                    setFormError('');
                    // Reset the referral dropdown state
                    setEditCustomerData(prev => ({
                      ...prev,
                      showReferralDropdown: false,
                      filteredReferralOptions: []
                    }));
                  }}
                  disabled={formLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={formLoading || !emailValidation.isValid || !usernameValidation.isValid}
                >
                  {formLoading ? 'Updating...' : 'Update Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Promote To Coach Modal */}
      {showPromoteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎓</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Promote Customer to Coach')}
            </h3>

            {promoteError && (
              <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                {promoteError}
              </div>
            )}

            <div style={{ 
              background: 'var(--pale-blue)', 
              padding: '20px', 
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                {t('Customer Details')}:
              </h4>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Name')}:</strong> {customer?.fullName}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Email')}:</strong> {customer?.email}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Current Role')}:</strong> {customer?.memberRole?.displayName || t('Customer')}
              </div>
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('Status')}:</strong> {customer?.status}
              </div>
            </div>

            <div style={{ 
              background: 'var(--gray-light)', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              color: 'var(--gray-dark)'
            }}>
              <strong>{t('Important')}:</strong> {t('Promoting this customer to coach will')}:
              <ul style={{ marginTop: '10px', marginLeft: '20px', textAlign: 'left' }}>
                <li>{t('Change their role from Customer to Coach')}</li>
                <li>{t('Grant them access to coach features')}</li>
                <li>{t('Allow them to manage their own customers')}</li>
                <li>{t('This action cannot be easily undone')}</li>
              </ul>
            </div>

            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {t('Are you sure you want to send a promotion request for {name} to become a coach?', { name: customer?.fullName || '' })}
            </p>
            
            <div style={{ 
              background: 'var(--pale-blue)', 
              padding: '15px', 
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
              textAlign: 'left'
            }}>
              <strong>{t('What happens next')}:</strong>
              <ul style={{ marginTop: '10px', marginLeft: '20px' }}>
                <li>{t('Upline Tab Team will receive an email notification')}</li>
                <li>{t('They will also see a notification in their dashboard')}</li>
                <li>{t('An approver will review and either approve or reject the request')}</li>
                <li>{t('You will be notified of the decision')}</li>
                <li>{t('If approved, all customers referred by {name} will become your direct customers', { name: customer?.fullName || '' })}</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowPromoteModal(false);
                  setPromoteError('');
                }}
                disabled={promoteLoading}
              >
                {t('Cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handlePromoteToCoach}
                disabled={promoteLoading}
                style={{ 
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                  border: 'none'
                }}
              >
                {promoteLoading ? t('Promoting...') : t('Promote to Coach')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Partial Payment Modal */}
      {showPartialPaymentModal && selectedInvoiceForPayment && (
        <PartialPaymentModal
          invoice={selectedInvoiceForPayment}
          onClose={() => {
            setShowPartialPaymentModal(false);
            setSelectedInvoiceForPayment(null);
          }}
          onPaymentAdded={handlePartialPaymentAdded}
        />
      )}

      {/* Cashback Modal */}
      {showCashbackModal && cashbackBalance && (
        <CashbackModal
          isOpen={showCashbackModal}
          onClose={() => setShowCashbackModal(false)}
          cashbackBalance={cashbackBalance.availableBalance}
          currentSubtotal={parseFloat(gstCalculations.subtotalBeforeCashback) || 0}
          onApplyCashback={handleApplyCashback}
          appliedCashback={appliedCashback}
        />
      )}

      {/* Custom Cashback Modal */}
      {showCustomCashbackModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '8px',
            padding: '24px',
            width: '100%',
            maxWidth: '560px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>{t('Add Cashback')}</h3>
              <button
                type="button"
                onClick={() => setShowCustomCashbackModal(false)}
                style={{ border: 'none', background: 'transparent', fontSize: '24px', cursor: 'pointer', color: 'var(--gray-dark)' }}
              >
                x
              </button>
            </div>

            {customCashbackError && <div className="alert alert-error">{customCashbackError}</div>}
            {customCashbackSuccess && <div className="alert alert-success">{customCashbackSuccess}</div>}

            {pendingCustomCashbackRequests.length > 0 && (
              <div style={{ marginBottom: '18px' }}>
                <h4 style={{ color: 'var(--primary-blue)', marginBottom: '10px' }}>{t('Pending Cashback Requests')}</h4>
                {pendingCustomCashbackRequests.map((request) => (
                  <div
                    key={request.id}
                    style={{
                      border: '1px solid var(--gray-medium)',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '10px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: '12px',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600' }}>₹{Number(request.amount || 0).toFixed(2)}</div>
                      <div style={{ fontSize: '13px', color: 'var(--gray-dark)' }}>{request.description}</div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        {t('OTP sent to')}: {request.otpSentTo || '-'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => selectPendingCustomCashbackRequest(request)}
                      disabled={customCashbackLoading}
                    >
                      {t('Verify')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {customCashbackStep === 'details' ? (
              <form onSubmit={handleRequestCustomCashback}>
                <div className="form-group">
                  <label className="form-label">{t('Cashback Amount')} *</label>
                  <input
                    type="number"
                    min="1"
                    step="0.01"
                    className="form-input"
                    value={customCashbackForm.amount}
                    onChange={(e) => setCustomCashbackForm({ ...customCashbackForm, amount: e.target.value })}
                    required
                    disabled={customCashbackLoading}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('Description')} *</label>
                  <textarea
                    className="form-input"
                    rows="4"
                    value={customCashbackForm.description}
                    onChange={(e) => setCustomCashbackForm({ ...customCashbackForm, description: e.target.value })}
                    required
                    disabled={customCashbackLoading}
                    placeholder={t('Reason for adding cashback')}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowCustomCashbackModal(false)}>
                    {t('Cancel')}
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={customCashbackLoading}>
                    {customCashbackLoading ? t('Sending OTP...') : t('Send OTP')}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleVerifyCustomCashback}>
                <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--pale-blue)', borderRadius: '8px' }}>
                  <div><strong>{t('Amount')}:</strong> ₹{Number(customCashbackRequest?.amount || customCashbackForm.amount || 0).toFixed(2)}</div>
                  <div><strong>{t('Description')}:</strong> {customCashbackRequest?.description || customCashbackForm.description}</div>
                  <div><strong>{t('OTP sent to')}:</strong> {customCashbackRequest?.otpSentTo || '-'}</div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('6 Digit OTP')} *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength="6"
                    className="form-input"
                    value={customCashbackForm.otp}
                    onChange={(e) => setCustomCashbackForm({ ...customCashbackForm, otp: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                    required
                    disabled={customCashbackLoading}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setCustomCashbackStep('details')}>
                    {t('Back')}
                  </button>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="button" className="btn btn-secondary" onClick={() => setShowCustomCashbackModal(false)}>
                      {t('Close')}
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={customCashbackLoading || customCashbackForm.otp.length !== 6}>
                      {customCashbackLoading ? t('Verifying...') : t('Verify & Add')}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Assign Program Modal */}
      {showAssignProgramModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Assign Program to {name}', { name: customer?.fullName || '' })}
            </h3>

            {assignProgramError && (
              <div className="alert alert-error">{assignProgramError}</div>
            )}

            <form onSubmit={handleAssignProgram}>
              <div className="form-group">
                <label htmlFor="programId" className="form-label">
                  {t('Select Program')} *
                </label>
                <select
                  id="programId"
                  className="form-input"
                  value={assignProgramData.programId}
                  onChange={(e) => setAssignProgramData({...assignProgramData, programId: e.target.value})}
                  required
                  disabled={assignProgramLoading}
                >
                  <option value="">{t('Select Program')}</option>
                  {programs
                    .filter(program => program.programType === customer?.customerType)
                    .map(program => (
                      <option key={program.id} value={program.id}>
                        {program.programName} ({program.programCode}) - {program.days} days
                      </option>
                    ))}
                </select>
                <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '5px' }}>
                  {t('Showing {type} programs only', { type: customer?.customerType || '' })}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="startDate" className="form-label">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-input"
                    value={assignProgramData.startDate}
                    onChange={(e) => setAssignProgramData({...assignProgramData, startDate: e.target.value})}
                    required
                    disabled={assignProgramLoading}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endDate" className="form-label">
                    End Date *
                  </label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-input"
                    value={assignProgramData.endDate}
                    onChange={(e) => setAssignProgramData({...assignProgramData, endDate: e.target.value})}
                    required
                    disabled={assignProgramLoading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="comments" className="form-label">
                  {t('Comments')}
                </label>
                <textarea
                  id="comments"
                  className="form-input"
                  rows="3"
                  value={assignProgramData.comments}
                  onChange={(e) => setAssignProgramData({...assignProgramData, comments: e.target.value})}
                  disabled={assignProgramLoading}
                  placeholder={t('Additional comments about program assignment...')}
                />
              </div>

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAssignProgramModal(false);
                    setAssignProgramError('');
                    setAssignProgramData({
                      programId: '',
                      startDate: getCurrentDateForInput(),
                      endDate: '',
                      comments: ''
                    });
                  }}
                  disabled={assignProgramLoading}
                >
                  {t('Cancel')}
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={assignProgramLoading}
                >
                  {assignProgramLoading ? t('Assigning...') : t('Assign Now')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Stop Program Modal */}
      {showStopProgramModal && selectedProgramToStop && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Stop Program')}
            </h3>

            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                {t('Program Details')}
              </h4>
              <div style={{ fontSize: '14px' }}>
                <div><strong>{t('Program')}:</strong> {selectedProgramToStop.programName}</div>
                <div><strong>{t('Code')}:</strong> {selectedProgramToStop.programCode}</div>
                <div><strong>{t('Start Date')}:</strong> {formatDateForDisplay(selectedProgramToStop.startDate)}</div>
                <div><strong>{t('End Date')}:</strong> {formatDateForDisplay(selectedProgramToStop.endDate)}</div>
                <div><strong>{t('Volume Points')}:</strong> {selectedProgramToStop.volumePointConsumption || 0} / {selectedProgramToStop.totalVolumePoints || 0}</div>
                <div><strong>{t('Balance Volume Points')}:</strong> {(selectedProgramToStop.totalVolumePoints || 0) - (selectedProgramToStop.volumePointConsumption || 0)}</div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="stopComments" className="form-label">
                {t('Reason for Stopping Program')} *
              </label>
              <textarea
                id="stopComments"
                className="form-input"
                rows="3"
                value={stopProgramComments}
                onChange={(e) => setStopProgramComments(e.target.value)}
                required
                disabled={stopProgramLoading}
                placeholder={t('Please provide reason for stopping this program...')}
              />
            </div>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowStopProgramModal(false);
                  setSelectedProgramToStop(null);
                  setStopProgramComments('');
                }}
                disabled={stopProgramLoading}
              >
                {t('Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleStopProgram}
                disabled={stopProgramLoading || !stopProgramComments.trim()}
                style={{ 
                  background: 'var(--error)',
                  border: 'none'
                }}
              >
                {stopProgramLoading ? t('Stopping...') : t('Stop Program')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Program Attendance Modal */}
      {showAttendanceModal && selectedProgramAttendance && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                📅 Program Attendance
              </h3>
              <button
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedProgramAttendance(null);
                  setAttendanceDates([]);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                Program Details
              </h4>
              <div style={{ fontSize: '14px' }}>
                <div><strong>{t('Program')}:</strong> {selectedProgramAttendance.programName}</div>
                <div><strong>{t('Code')}:</strong> {selectedProgramAttendance.programCode}</div>
                <div><strong>{t('Duration')}:</strong> {formatDateForDisplay(selectedProgramAttendance.startDate)} {t('to')} {formatDateForDisplay(selectedProgramAttendance.endDate)}</div>
                <div><strong>{t('Days Attended')}:</strong> {selectedProgramAttendance.daysAttended || 0}</div>
              </div>
            </div>

            {attendanceLoading ? (
              <div className="loading">{t('Loading attendance data...')}</div>
            ) : attendanceDates.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
                {t('No attendance records found for this program period.')}
              </div>
            ) : (
              <div>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                  {t('Attendance Dates ({count} days)', { count: attendanceDates.length })}
                </h4>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: '10px',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  padding: '10px',
                  border: '1px solid var(--gray-medium)',
                  borderRadius: '6px'
                }}>
                  {attendanceDates.map((date, index) => (
                    <div
                      key={index}
                      style={{
                        background: 'var(--success)',
                        color: 'white',
                        padding: '8px',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {formatDateForDisplay(date)}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowAttendanceModal(false);
                  setSelectedProgramAttendance(null);
                  setAttendanceDates([]);
                }}
              >
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Meal Plan Modal */}
      {showAddMealPlan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {editingMealPlan
                ? t('Edit Meal Plan')
                : t('Create Meal Plan for {name}', { name: customer?.fullName || '' })}
            </h3>

            {mealPlanError && (
              <div className="alert alert-error">{mealPlanError}</div>
            )}

            <form onSubmit={handleSaveMealPlan}>
              {/* Criteria Selection */}
              <div style={{
                border: '2px solid var(--primary-blue)',
                borderRadius: '8px',
                padding: '20px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Select Criteria')}</h4>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div className="form-group">
                    <label htmlFor="weightCategory" className="form-label">
                      Weight Category *
                    </label>
                    <select
                      id="weightCategory"
                      className="form-input"
                      value={selectedWeightCategory}
                      onChange={(e) => handleMealPlanCriteriaChange(e.target.value, selectedFoodPreference)}
                      required
                      disabled={mealPlanLoading}
                    >
                      <option value="">Select Weight Category</option>
                      {weightCategories.map(category => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="foodPreference" className="form-label">
                      Food Preference *
                    </label>
                    <select
                      id="foodPreference"
                      className="form-input"
                      value={selectedFoodPreference}
                      onChange={(e) => handleMealPlanCriteriaChange(selectedWeightCategory, e.target.value)}
                      required
                      disabled={mealPlanLoading}
                    >
                      <option value="">Select Food Preference</option>
                      {foodPreferences.map(preference => (
                        <option key={preference} value={preference}>
                          {preference.replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Template Selection */}
              {availableTemplates.length > 0 && (
                <div style={{
                  border: '2px solid var(--success)',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Available Templates')}</h4>
                  <div style={{ fontSize: '14px', color: 'var(--success)', marginBottom: '10px' }}>
                    ✅ Found {availableTemplates.length} template(s) matching your criteria
                  </div>
                  
                  {availableTemplates.map(template => (
                    <div
                      key={template.id}
                      style={{
                        border: selectedTemplate?.id === template.id ? '2px solid var(--primary-blue)' : '1px solid var(--gray-medium)',
                        borderRadius: '6px',
                        padding: '15px',
                        marginBottom: '10px',
                        cursor: 'pointer',
                        background: selectedTemplate?.id === template.id ? 'var(--pale-blue)' : 'var(--white)'
                      }}
                      onClick={() => {
                        setSelectedTemplate(template);
                        // Create completely new meal plan items based on template values
                        const newMealPlanItems = (template.items || []).map((item, index) => ({
                          timeSlot: item.timeSlot || '',
                          description: item.description || '',
                          notes: item.notes || '',
                          sortOrder: item.sortOrder || (index + 1)
                        }));
                        
                        // Set new meal plan data (this will be saved to customer_meal_plan_item table)
                        setMealPlanData({
                          mealPlanName: `${customer?.fullName} - ${template.templateName}`,
                          weightCategory: selectedWeightCategory,
                          foodPreference: selectedFoodPreference,
                          instructions: template.instructions || '',
                          items: newMealPlanItems
                        });
                      }}
                    >
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {template.templateName}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--gray-dark)' }}>
                        {template.items?.length || 0} meal items • {template.weightCategory} • {template.foodPreference}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Meal Plan Details */}
              {(selectedTemplate || editingMealPlan) && (
                <div style={{
                  border: '2px solid var(--gray-medium)',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px'
                }}>
                  <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>{t('Meal Plan Details')}</h4>
                  
                  <div className="form-group">
                    <label htmlFor="mealPlanName" className="form-label">
                      Meal Plan Name *
                    </label>
                    <input
                      type="text"
                      id="mealPlanName"
                      className="form-input"
                      value={mealPlanData.mealPlanName}
                      onChange={(e) => setMealPlanData({...mealPlanData, mealPlanName: e.target.value})}
                      required
                      disabled={mealPlanLoading}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="mealPlanInstructions" className="form-label">
                      Instructions
                    </label>
                    <textarea
                      id="mealPlanInstructions"
                      className="form-input"
                      rows="4"
                      value={mealPlanData.instructions || ''}
                      onChange={(e) => setMealPlanData({...mealPlanData, instructions: e.target.value})}
                      disabled={mealPlanLoading}
                      placeholder="Optional customer-specific instructions. You can override the template instructions here."
                    />
                  </div>

                  {/* Meal Items */}
                  {mealPlanData.items && (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <h5 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                          Meal Items ({mealPlanData.items.length}) - You can modify these values
                        </h5>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={addMealPlanItem}
                          disabled={mealPlanLoading}
                          style={{ fontSize: '12px', padding: '6px 10px' }}
                        >
                          Add Item
                        </button>
                      </div>
                      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {mealPlanData.items.map((item, index) => (
                          <div
                            key={index}
                            style={{
                              border: '1px solid var(--gray-medium)',
                              borderRadius: '6px',
                              padding: '15px',
                              marginBottom: '10px',
                              background: 'var(--white)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                              <div style={{ fontWeight: 'bold', color: 'var(--primary-blue)' }}>
                                Item #{index + 1}
                              </div>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => removeMealPlanItem(index)}
                                disabled={mealPlanLoading}
                                style={{ fontSize: '11px', padding: '4px 8px', color: 'var(--error)' }}
                              >
                                Remove
                              </button>
                            </div>
                                
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                  Time Slot *
                                </label>
                                <input
                                  type="text"
                                  className="form-input"
                                  value={item.timeSlot || ''}
                                  onChange={(e) => updateMealPlanItem(index, 'timeSlot', e.target.value)}
                                  placeholder="e.g., On waking up, Breakfast, etc."
                                  disabled={mealPlanLoading}
                                  required
                                />
                              </div>
                              <div className="form-group" style={{ margin: 0 }}>
                                <label className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                  Sort Order
                                </label>
                                <input
                                  type="number"
                                  className="form-input"
                                  value={item.sortOrder || index + 1}
                                  onChange={(e) => updateMealPlanItem(index, 'sortOrder', parseInt(e.target.value) || index + 1)}
                                  disabled={mealPlanLoading}
                                  min="1"
                                />
                              </div>
                            </div>
                                
                            <div className="form-group" style={{ margin: 0, marginBottom: '10px' }}>
                              <label className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                Description *
                              </label>
                              <textarea
                                className="form-input"
                                rows="2"
                                value={item.description || ''}
                                onChange={(e) => updateMealPlanItem(index, 'description', e.target.value)}
                                placeholder="e.g., Start your day with a mug of Afresh."
                                disabled={mealPlanLoading}
                                required
                              />
                            </div>
                                
                            <div className="form-group" style={{ margin: 0 }}>
                              <label className="form-label" style={{ fontSize: '12px', marginBottom: '5px' }}>
                                Notes
                              </label>
                              <textarea
                                className="form-input"
                                rows="2"
                                value={item.notes || ''}
                                onChange={(e) => updateMealPlanItem(index, 'notes', e.target.value)}
                                placeholder="e.g., Note: Mix 2 Spoons Afresh in 200 ml warm water"
                                disabled={mealPlanLoading}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowAddMealPlan(false);
                    setMealPlanError('');
                    resetMealPlanForm();
                  }}
                  disabled={mealPlanLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={mealPlanLoading || (!selectedTemplate && !editingMealPlan)}
                >
                  {mealPlanLoading
                    ? (editingMealPlan ? t('Saving...') : t('Creating...'))
                    : (editingMealPlan ? t('Save Changes') : t('Create Meal Plan'))}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Share Meal Plan Modal */}
      {showShareModal && selectedMealPlanForShare && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '500px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>📱</div>
            <h3 style={{ color: 'var(--dark-blue)', marginBottom: '20px' }}>
              {t('Share Meal Plan via WhatsApp')}
            </h3>

            <div style={{
              background: 'var(--pale-blue)',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px',
              textAlign: 'left'
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                {t('Meal Plan Details')}:
              </div>
              <div style={{ fontSize: '14px' }}>
                <div><strong>Name:</strong> {selectedMealPlanForShare.mealPlanName}</div>
                <div><strong>Customer:</strong> {customer?.fullName}</div>
                <div><strong>Phone:</strong> {customer?.phone}</div>
                <div><strong>Weight Category:</strong> {selectedMealPlanForShare.weightCategory}</div>
                <div><strong>Food Preference:</strong> {selectedMealPlanForShare.foodPreference}</div>
              </div>
            </div>

            <p style={{ color: 'var(--gray-dark)', marginBottom: '25px' }}>
              {t("This will send the meal plan link to the customer's WhatsApp number.")}
            </p>

            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowShareModal(false);
                  setSelectedMealPlanForShare(null);
                }}
              >
                {t('Cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmShareMealPlan}
                style={{ 
                  background: 'linear-gradient(135deg, #48BB78 0%, #38A169 100%)',
                  border: 'none'
                }}
              >
                📱 {t('Share via WhatsApp')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Program Details Modal */}
      {showProgramDetailsModal && selectedProgramForDetails && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'var(--white)',
            borderRadius: '12px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
                📋 {t('Program Details')}
              </h3>
              <button
                onClick={() => {
	                  setShowProgramDetailsModal(false);
	                  setSelectedProgramForDetails(null);
	                  setProgramDetailsComments('');
	                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ×
              </button>
            </div>

            <div style={{
              border: '2px solid var(--primary-blue)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '15px' }}>
                {t('Program Information')}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px' }}>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Program Name')}:</strong> {selectedProgramForDetails.programName}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Program Code')}:</strong> {selectedProgramForDetails.programCode}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Start Date')}:</strong> {formatDateForDisplay(selectedProgramForDetails.startDate)}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('End Date')}:</strong> {formatDateForDisplay(selectedProgramForDetails.endDate)}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Status')}:</strong> 
                    <span style={{
                      background: selectedProgramForDetails.isActive ? 'var(--success)' : 'var(--gray-medium)',
                      color: selectedProgramForDetails.isActive ? 'white' : 'var(--gray-dark)',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      marginLeft: '8px'
                    }}>
                      {selectedProgramForDetails.scheduled ? 'Scheduled' : selectedProgramForDetails.isActive ? t('Active') : t('Inactive')}
                    </span>
                  </div>
                </div>
                <div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Assigned By')}:</strong> {selectedProgramForDetails.assignedBy}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Assigned Date')}:</strong> {selectedProgramForDetails.assignedAt ? new Date(selectedProgramForDetails.assignedAt).toLocaleDateString() : t('N/A')}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Days Attended')}:</strong> {selectedProgramForDetails.daysAttended || 0}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Volume Points')}:</strong> {selectedProgramForDetails.volumePointConsumption || 0} / {selectedProgramForDetails.totalVolumePoints || 0}
                  </div>
                  <div style={{ marginBottom: '8px' }}>
                    <strong>{t('Balance Points')}:</strong> {(selectedProgramForDetails.totalVolumePoints || 0) - (selectedProgramForDetails.volumePointConsumption || 0)}
                  </div>
                </div>
              </div>
            </div>

            {user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') && <div style={{border:'1px solid #ddd',padding:16,marginBottom:20}}>
              <h4>Edit program dates</h4>
              <label>Start date<input className="form-input" type="date" value={programDates.startDate} disabled={programDetailsSaving} onChange={e=>setProgramDates(d=>({...d,startDate:e.target.value}))}/></label>
              <label>End date<input className="form-input" type="date" value={programDates.endDate} disabled={programDetailsSaving} onChange={e=>setProgramDates(d=>({...d,endDate:e.target.value}))}/></label>
              <p>Recorded attendance and guest consumption must remain within the dates. Editing dates does not reopen a stopped program.</p>
              {programDateMessage && <div role="alert">{String(programDateMessage)}</div>}
              <button className="btn btn-primary" disabled={programDetailsSaving} onClick={handleSaveProgramDates}>{programDetailsSaving?'Saving…':'Save program dates'}</button>
            </div>}
            {/* Assignment Comments */}
            {(selectedProgramForDetails.comments || (!selectedProgramForDetails.isActive && user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR'))) && (
              <div style={{
                border: '2px solid var(--success)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  📝 {t('Assignment Comments')}
                </h4>
                {!selectedProgramForDetails.isActive && user?.systemRoles?.some(role => role.code === 'SYSTEM_EDITOR') ? (
                  <>
                    <textarea
                      className="form-input"
                      value={programDetailsComments}
                      onChange={(e) => setProgramDetailsComments(e.target.value)}
                      rows="4"
                      disabled={programDetailsSaving}
                      placeholder={t('Additional comments about program assignment...')}
                      style={{ resize: 'vertical', marginBottom: '12px' }}
                    />
                    <button
                      className="btn btn-primary"
                      onClick={handleSaveProgramComments}
                      disabled={programDetailsSaving}
                    >
                      {programDetailsSaving ? t('Saving...') : t('Save Comments')}
                    </button>
                  </>
                ) : (
                  <div style={{
                    background: 'var(--pale-blue)',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    lineHeight: '1.5'
                  }}>
                    {selectedProgramForDetails.comments}
                  </div>
                )}
              </div>
            )}

            {/* Close Comments (if program is stopped) */}
            {!selectedProgramForDetails.isActive && selectedProgramForDetails.closeComments && (
              <div style={{
                border: '2px solid var(--warning)',
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '20px'
              }}>
                <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                  🛑 {t('Program Stop Reason')}
                </h4>
                <div style={{
                  background: 'var(--gray-light)',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  lineHeight: '1.5'
                }}>
                  {selectedProgramForDetails.closeComments}
                </div>
                {selectedProgramForDetails.closedAt && (
                  <div style={{ fontSize: '12px', color: 'var(--gray-dark)', marginTop: '8px' }}>
                    {t('Stopped on')}: {new Date(selectedProgramForDetails.closedAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            )}

            {/* Program Duration Info */}
            <div style={{
              border: '1px solid var(--gray-medium)',
              borderRadius: '8px',
              padding: '15px',
              background: 'var(--gray-light)'
            }}>
              <h4 style={{ color: 'var(--dark-blue)', marginBottom: '10px' }}>
                📊 {t('Program Statistics')}
              </h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
                <div>
                  <strong>{t('Total Duration')}:</strong> {(() => {
                    const start = new Date(selectedProgramForDetails.startDate);
                    const end = new Date(selectedProgramForDetails.endDate);
                    const diffTime = Math.abs(end - start);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return t('{count} days', { count: diffDays });
                  })()}
                </div>
                <div>
                  <strong>{t('Attendance Rate')}:</strong> {(() => {
                    const start = new Date(selectedProgramForDetails.startDate);
                    const end = new Date(selectedProgramForDetails.endDate);
                    const today = new Date();
                    const actualEnd = selectedProgramForDetails.isActive ? (today < end ? today : end) : end;
                    const diffTime = Math.abs(actualEnd - start);
                    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    const attendedDays = selectedProgramForDetails.daysAttended || 0;
                    const rate = totalDays > 0 ? ((attendedDays / totalDays) * 100).toFixed(1) : 0;
                    return `${rate}%`;
                  })()}
                </div>
                <div>
                  <strong>{t('Points Utilization')}:</strong> {(() => {
                    const total = selectedProgramForDetails.totalVolumePoints || 0;
                    const consumed = selectedProgramForDetails.volumePointConsumption || 0;
                    const rate = total > 0 ? ((consumed / total) * 100).toFixed(1) : 0;
                    return `${rate}%`;
                  })()}
                </div>
                <div>
                  <strong>{t('Days Remaining')}:</strong> {(() => {
                    if (!selectedProgramForDetails.isActive) return t('Program Completed');
                    const end = new Date(selectedProgramForDetails.endDate);
                    const today = new Date();
                    if (today > end) return t('Program Expired');
                    const diffTime = Math.abs(end - today);
                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    return t('{count} days', { count: diffDays });
                  })()}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="btn btn-primary"
	                onClick={() => {
	                  setShowProgramDetailsModal(false);
	                  setSelectedProgramForDetails(null);
	                  setProgramDetailsComments('');
	                }}
              >
                {t('Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Club Change Modal */}
      {showClubChangeModal && (
        <ClubChangeModal 
          user={customer} 
          onClose={() => setShowClubChangeModal(false)} 
        />
      )}
    </div>
  );
};

export default CustomerDetails;
